import moment from 'moment';
import { convertDecimalToTime, formatNumberWithCommas, getAxisDisplayMax, getPercentageOfTwoNumbers } from 'utils/common-util';
import { conversionUnitProp } from './type';
import type { Theme } from '@mui/material/styles';

function formatByByte(selectByte: string, kpiValue: any) {
	if (selectByte === 'PB') {
		return kpiValue;
	}

	if (selectByte === 'TB') {
		return kpiValue * 1024;
	}

	if (selectByte === 'GB') {
		return kpiValue * 1024 * 1024;
	}

	return kpiValue;
}

function formatNumber(number: any) {
	if (number != null && !isNaN(number)) {
		return Number(number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	} else {
		return '0.00';
	}
}

function formatNumberM(number: any) {
	if (number != null && !isNaN(number) && number !== 0) {
		return Number(number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	} else {
		return '0';
	}
}

const formatNumberUnits = (number: number) => {
	if (number >= 1000000000) {
		return `${new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
			number / 1000000000
		)}B`;
	}
	if (number >= 100000) {
		return `${new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
			number / 1000000
		)}MM`;
	}
	if (number >= 1000) {
		return `${formatNumberWithCommas((number / 1000).toFixed(2), true)}K`;
	}
	return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(number);
};

const formatNumberPBI = (number: number) => {
	if (number >= 1000)
		return `${formatNumberWithCommas((number / 1000), false)}K`;
	else
		return `${formatNumberWithCommas(number.toFixed(2))}`;
};

const formatNumberByUnits = (number: number) => {
	if (number >= 1000000) {
		return `${new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
			number / 1000000
		)}MM`;
	}
	if (number >= 1000) {
		return `${formatNumberWithCommas((number / 1000).toFixed(2), true)}K`;
	}
	return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(number);
};

export const getDefaultOptions = (isLightTrend?: boolean) => ({
	title: {
		show: true,
		textStyle: {
			color: isLightTrend ? '#718096' : '#bcbcbc',
			fontWeight: 'normal',
			fontSize: 16
		},
		text: 'No data',
		left: 'center',
		top: 'center'
	},
	backgroundColor: isLightTrend ? 'transparent' : '#053367'
});

export const defaultOptions = getDefaultOptions(false);

type GetChartOptionsProps = {
	chartData: any;
	selectedByte: conversionUnitProp;
	conversionUnit: string;
	chartInterval: number | 'auto';
	lastUpdatedDate: string;
	isDatabrick: boolean;
	isForeCast: boolean;
	isYearly: boolean;
	isFinops: boolean;
	isHappySignal: boolean;
	lastYearAvgData: any;
	hasNoFormat: boolean;
	relevantYear: number | string;
	themeMode?: 'dark' | 'lightTrend';
	isDataEstateTrend?: boolean;
	isDataEstateQuery?: boolean;
	lineColor?: string;
	title?: string;
};

const getPreviousYearMonthMeta = () => {
	let currentMonth = new Date().getMonth();
	if (currentMonth === 0) currentMonth = 12;

	const currentYear = new Date().getFullYear();
	const lastMonthString = currentMonth.toString().padStart(2, '0');

	return {
		filterMonth: `${currentYear - 1}-${lastMonthString}`,
		lastMonthString,
		currentYear,
	};
};

const getCurrentMonthIndex = (
	allMonths: string[],
	currentDate: string,
	currentDateYear: string,
	isYearly: boolean
) => {
	let index = allMonths.findIndex(
		(month: string) => month === currentDate
	);

	if (index === -1) {
		index = allMonths.length - 1;
	}
	if (isYearly) {
		index = allMonths.findIndex(
			(year: string) => year === currentDateYear
		);
	}

	return index;
};

const getRotateValue = (hasNoFormat: boolean) => {
	const isSmallScreen = window.innerWidth < 600;

	if (hasNoFormat) return 0;
	if (isSmallScreen) return 50;
	return 30;
};

const buildXAxis = (
	allMonths: string[],
	hasNoFormat: boolean,
	chartInterval: number | 'auto',
	rotateValue: number,
	isYearly: boolean,
	isLightTrend?: boolean
) => ({
	type: 'category',
	splitLine: {
		show: false,
	},
	axisLine: {
		show: false,
	},
	axisTick: { show: false },
	data: allMonths,
	boundaryGap: hasNoFormat,
	axisLabel: {
		interval: chartInterval,
		hideOverlap: true,
		rotate: isLightTrend ? 0 : rotateValue,
		fontSize: window.innerWidth < 600 ? 11 : 12,
		color: isLightTrend ? '#718096' : '#A7ADCB',
		fontFamily: 'Montserrat, sans-serif',
		formatter: (value: any) => {
			if (isYearly) return value;
			if (isLightTrend) {
				return moment(value).format('MMM YYYY');
			}
			return moment(value).format('MMM-YY');
		},
	},
});

const buildYAxis = (
	selectedByte: conversionUnitProp,
	conversionUnit: string,
	isDatabrick: boolean,
	hasNoFormat: boolean,
	isHappySignal: boolean,
	isLightTrend?: boolean
) => ({
	type: 'value',
	...(isHappySignal && { min: -100, max: 100 }),
	...(isLightTrend && {
		name: 'value in Million Dollars',
		nameLocation: 'middle',
		nameGap: 36,
		nameRotate: 90,
		nameTextStyle: {
			color: '#718096',
			fontSize: 11,
			fontFamily: 'Montserrat, sans-serif',
		},
	}),
	splitLine: {
		show: true,
		lineStyle: {
			type: isLightTrend ? 'dotted' : 'dashed',
			color: isLightTrend ? '#CBD5E1' : '#004477',
		},
	},
	axisLine: { show: false },
	axisTick: { show: false },
	axisLabel: {
		color: isLightTrend ? '#718096' : '#A7ADCB',
		fontSize: window.innerWidth < 600 ? 11 : 12,
		margin: 10,
		rotate: 0,
		fontFamily: 'Montserrat, sans-serif',
		formatter: (value: any) => {
			if (isLightTrend) {
				if (value === 0) {
					return '0'
				};
				const millions = value / 1000000;
				return Number.isInteger(millions) ? millions.toString() : Number.parseFloat(millions.toFixed(2)).toString();
			}
			if (selectedByte === 'byte') {
				const optValue = formatByByte(conversionUnit, value);
				return formatNumberUnits(optValue);
			}
			if (selectedByte === 'cost' || isDatabrick) {
				return `$${formatNumberUnits(value)}`;
			}
			return hasNoFormat ? value : formatNumberUnits(value);
		},
	},
});

const buildTooltip = (
	selectedByte: conversionUnitProp,
	conversionUnit: string,
	hasNoFormat: boolean,
	isYearly: boolean,
	isLightTrend?: boolean
) => ({
	trigger: 'axis',
	formatter: (params: any) => {
		if (isLightTrend && Array.isArray(params)) {
			const activeParam = params.find((p: any) => p.value && p.value[1] != null) || params[0];
			if (!activeParam || !activeParam.value) return '';
			const [dateVal, numVal] = activeParam.value;
			const dateLabel = moment(dateVal).format('MMM YYYY');
			const formattedVal = typeof numVal === 'number'
				? (numVal >= 1000000 ? `$${(numVal / 1000000).toFixed(2)} Million Dollars` : `$${numVal}`)
				: numVal;
			return `<b>${dateLabel}</b><br/>${activeParam.seriesName}: ${formattedVal}`;
		}
		const { value } = params[0];
		const dateLabel = isYearly
			? value[0]
			: moment(value[0]).format('MMM-YY');

		if (selectedByte === 'byte') {
			const optValue = formatByByte(conversionUnit, value[1]);
			return `${dateLabel}: ${formatNumberUnits(optValue)} ${conversionUnit}`;
		}

		if (selectedByte === 'cost') {
			return `${dateLabel}: $${formatNumberUnits(value[1])}`;
		}

		return `${dateLabel}: ${hasNoFormat ? value[1] : formatNumberUnits(value[1])}`;
	},
	backgroundColor: isLightTrend ? '#FFFFFF' : '#053367',
	borderColor: isLightTrend ? '#E2E8F0' : undefined,
	textStyle: { color: isLightTrend ? '#1E293B' : '#A7ADCB' },
});

const buildSeries = ({
	isHappySignal,
	relevantYear,
	lastYearAvgData,
	lineData,
	hasNoFormat,
	selectedByte,
	conversionUnit,
	isYearly,
	allMonths,
	currentMonthIndex,
	isFinops,
	lastUpdatedDate,
	formattedCurrentDate,
	isLightTrend,
	lineColor,
	isDataEstateQuery,
}: {
	isHappySignal: boolean;
	relevantYear: number | string;
	lastYearAvgData: any;
	lineData: any[];
	hasNoFormat: boolean;
	selectedByte: conversionUnitProp;
	conversionUnit: string;
	isYearly: boolean;
	allMonths: string[];
	currentMonthIndex: number;
	isFinops: boolean;
	lastUpdatedDate: string;
	formattedCurrentDate: string;
	isLightTrend?: boolean;
	lineColor?: string;
	isDataEstateQuery?: boolean;
}) => {
	if (isLightTrend && !isDataEstateQuery) {
		const splitIndex = currentMonthIndex >= 0 && currentMonthIndex < allMonths.length
			? currentMonthIndex
			: Math.max(0, Math.floor((allMonths.length - 1) / 2));

		const splitMonth = allMonths[splitIndex];

		const currentGrowthData = allMonths.map((month, idx) => {
			const item = lineData.find(([d]) => d === month);
			const val = item ? item[1] : null;
			if (idx <= splitIndex) return [month, val];
			return [month, null];
		});

		const forecastData = allMonths.map((month, idx) => {
			const item = lineData.find(([d]) => d === month);
			const val = item ? item[1] : null;
			if (idx >= splitIndex) return [month, val];
			return [month, null];
		});

		const currentGrowthSeries = {
			name: 'Current Growth',
			type: 'line',
			smooth: false,
			data: currentGrowthData,
			symbol: 'circle',
			symbolSize: 8,
			itemStyle: {
				color: lineColor || '#29B6F6',
			},
			lineStyle: {
				color: lineColor || '#29B6F6',
				width: 2.5,
			},
			markLine: {
				silent: true,
				symbol: 'none',
				lineStyle: {
					type: 'solid',
					color: '#FFFFFF',
					width: 2,
				},
				data: [{ xAxis: splitMonth }],
				label: { show: false },
			},
		};

		const forecastSeries = {
			name: 'Forecast',
			type: 'line',
			smooth: false,
			data: forecastData,
			symbol: 'circle',
			symbolSize: 8,
			itemStyle: {
				color: '#F4A236',
			},
			lineStyle: {
				color: '#F4A236',
				width: 2.5,
			},
		};

		return [currentGrowthSeries, forecastSeries];
	}

	const baseSeries: any = {
		name: isHappySignal ? 'Happiness Index' : 'Current Growth',
		type: 'line',
		smooth: false,
		data: lineData,
		itemStyle: {
			color: lineColor || '#003771',
		},
		lineStyle: {
			color: lineColor || '#003771',
			width: 2.5,
		},
		...(isHappySignal && {
			label: {
				show: true,
				position: 'top',
				fontSize: 12,
				color: '#F0F0F0',
			},
		}),
		animation: false,
		markPoint: {
			symbol: 'circle',
			data: hasNoFormat
				? lineData.map(([month, value]: [string, unknown], idx: number) => {
					const numValue =
						typeof value === 'number' ? value : Number(value);

					let offset: [number, number] = [-28, 10];
					if (idx === 0) offset = [10, 10];

					return {
						name: month,
						coord: [month, numValue],
						value: numValue,
						label: {
							show: true,
							position: 'top',
							offset,
							color: '#fff',
							formatter: (params: any) => {
								if (selectedByte === 'byte') {
									const number = formatNumberUnits(
										formatByByte(
											conversionUnit,
											params.value
										)
									);
									return `${number} ${conversionUnit}`;
								}
								if (selectedByte === 'cost') {
									return `$${formatNumberUnits(
										params.value
									)}`;
								}
								return params.value;
							},
						},
						symbolSize: 8,
					};
				})
				: [
					currentMonthIndex !== -1 &&
					!hasNoFormat && {
						name: isYearly
							? 'Current Year'
							: 'Current Month',
						coord: [
							allMonths[currentMonthIndex],
							lineData[currentMonthIndex][1],
						],
						value: lineData[currentMonthIndex][1],
						itemStyle: {
							color: isDataEstateQuery ? (lineColor || '#003771') : '#FFA500',
						},
						label: {
							show: true,
							position: 'top',
							offset: [
								selectedByte === 'byte' ? -36 : -28,
								10,
							],
							color: isDataEstateQuery ? '#71758F' : '#fff',
							fontWeight: 'bold',
							formatter: (params: any) => {
								if (selectedByte === 'byte') {
									const number = formatNumberUnits(
										formatByByte(
											conversionUnit,
											params.value
										)
									);
									return `${number} ${conversionUnit}`;
								}
								if (selectedByte === 'cost') {
									return `$${formatNumberUnits(
										params.value
									)}`;
								}
								return hasNoFormat
									? params.value
									: formatNumberUnits(params.value);
							},
						},
						symbolSize: 8,
					},
				].filter(Boolean),
		},
		markLine:
			(isDataEstateQuery || currentMonthIndex === allMonths.length - 1) &&
			!hasNoFormat && {
				silent: true,
				symbol: 'none',
				lineStyle: {
					type: 'dotted',
					color: isDataEstateQuery ? '#94A3B8' : '#FFA500',
					width: 1,
				},
				data: [
					{
						xAxis: allMonths[currentMonthIndex >= 0 ? currentMonthIndex : allMonths.length - 1],
					},
				],
				label: {
					formatter: () => {
						if (isFinops) return '';

						let dateToDisplay = formattedCurrentDate;

						if (
							lastUpdatedDate !== '' &&
							lastUpdatedDate !== undefined &&
							lastUpdatedDate !== null
						) {
							dateToDisplay = lastUpdatedDate;
						}

						return `Last Update Date: ${dateToDisplay}`;
					},
					position: 'end',
					color: isDataEstateQuery ? '#71758F' : '#fff',
					fontWeight: 'normal',
					align: 'right',
				},
			},
	};

	const finalSeries = [baseSeries];

	if (isHappySignal) {
		finalSeries.push({
			name: `${Number(relevantYear) - 1} Average`,
			label: {
				show: true,
				position: 'top',
				fontSize: 12,
				color: '#00F0F0',
			},
			type: 'line',
			data: lastYearAvgData,
			symbol: 'none',
			color: '#FFC239',
			lineStyle: { width: 2 },
		});
	}

	return finalSeries;
};

export const buildDataEstateTrendOptions = ({
	chartData = {},
	selectedByte,
	conversionUnit = 'TB',
	lastUpdatedDate,
	title = '',
	lineColor = '#003771',
}: {
	chartData: any;
	selectedByte?: conversionUnitProp;
	conversionUnit?: string;
	lastUpdatedDate?: string;
	title?: string;
	lineColor?: string;
}) => {
	if (!chartData || Object.keys(chartData).length === 0) {
		return {
			chartOption: {
				title: {
					show: true,
					textStyle: { color: '#718096', fontSize: 14, fontFamily: 'Montserrat, sans-serif' },
					text: 'No data available',
					left: 'center',
					top: 'center'
				},
				backgroundColor: 'transparent'
			},
			isDisplayChart: false
		};
	}

	const allEntries = Object.entries(chartData);
	const xCategories = allEntries.map(([dateStr]) => {
		if (dateStr.length === 7) {
			return moment(dateStr, 'YYYY-MM').format('MMM YYYY');
		}
		return dateStr;
	});

	const seriesValues = allEntries.map(([_, val]) => typeof val === 'number' ? val : Number(val));

	let yAxisTitle = 'value in Million Dollars';
	if (selectedByte === 'byte') {
		yAxisTitle = `Data Stored in ${conversionUnit || 'TB'}`;
	}

	const formattedLatestDate = (lastUpdatedDate && lastUpdatedDate !== '')
		? (moment(lastUpdatedDate, ['YYYY-MM-DD', 'YYYY-MM']).isValid()
			? moment(lastUpdatedDate).format('MM/DD/YYYY')
			: lastUpdatedDate)
		: moment().format('MM/DD/YYYY');
	const latestDateText = `Latest by ${formattedLatestDate}`;

	const lastIndex = xCategories.length - 1;

	const chartOption = {
		backgroundColor: 'transparent',
		grid: {
			top: '20%',
			left: '16%',
			right: '8%',
			bottom: '15%',
			containLabel: false
		},
		tooltip: {
			trigger: 'axis',
			backgroundColor: 'rgba(255, 255, 255, 0.95)',
			borderColor: '#E2E8F0',
			textStyle: { color: '#1E293B', fontFamily: 'Montserrat, sans-serif' },
			formatter: (params: any) => {
				if (!params || !params.length) return '';
				const item = params[0];
				if (selectedByte === 'byte') {
					const optValue = formatByByte(conversionUnit, item.value);
					return `${item.name}<br/><b>${formatNumberUnits(optValue)} ${conversionUnit || 'TB'}</b>`;
				}
				if (selectedByte === 'cost') {
					return `${item.name}<br/><b>$${formatNumberUnits(item.value)}</b>`;
				}
				return `${item.name}<br/><b>${formatNumberUnits(item.value)}</b>`;
			}
		},
		xAxis: {
			type: 'category',
			data: xCategories,
			boundaryGap: true,
			axisLine: {
				lineStyle: { color: '#94A3B8', width: 1 }
			},
			axisTick: { show: false },
			axisLabel: {
				color: '#718096',
				fontSize: 11,
				fontFamily: 'Montserrat, sans-serif'
			}
		},
		yAxis: {
			type: 'value',
			name: yAxisTitle,
			nameLocation: 'middle',
			nameGap: 45,
			nameRotate: 90,
			nameTextStyle: {
				color: '#718096',
				fontSize: 11,
				fontFamily: 'Montserrat, sans-serif'
			},
			axisLine: { show: false },
			axisTick: { show: false },
			axisLabel: {
				color: '#718096',
				fontSize: 11,
				fontFamily: 'Montserrat, sans-serif',
				formatter: (value: any) => {
					if (value === 0) return '0';
					if (selectedByte === 'byte') {
						const optValue = formatByByte(conversionUnit, value);
						return formatNumberUnits(optValue);
					}
					const millions = value / 1000000;
					return Number.isInteger(millions) ? millions.toString() : Number.parseFloat(millions.toFixed(2)).toString();
				}
			},
			splitLine: {
				lineStyle: {
					type: 'dotted',
					color: '#E2E8F0'
				}
			}
		},
		series: [
			{
				type: 'line',
				data: seriesValues,
				smooth: false,
				symbol: 'circle',
				symbolSize: 14,
				itemStyle: {
					color: lineColor || '#003771',
					borderColor: lineColor || '#003771',
					borderWidth: 2,
					shadowColor: 'rgba(0, 55, 113, 0.35)',
					shadowBlur: 6,
					shadowOffsetY: 3
				},
				lineStyle: {
					color: lineColor || '#003771',
					width: 2.5
				},
				markLine: {
					silent: true,
					symbol: ['none', 'none'],
					lineStyle: {
						type: 'dotted',
						color: '#94A3B8',
						width: 1
					},
					data: [
						{
							xAxis: lastIndex >= 0 ? lastIndex : 0
						}
					],
					label: {
						show: true,
						position: 'end',
						formatter: latestDateText,
						color: '#718096',
						fontSize: 11,
						fontFamily: 'Montserrat, sans-serif',
						align: 'right'
					}
				}
			}
		]
	};

	return { chartOption, badgePercentage: null, chartTooltip: null };
};

export const getChartOptions = ({
	chartData = {},
	selectedByte,
	conversionUnit,
	chartInterval,
	lastUpdatedDate,
	isDatabrick,
	isForeCast,
	isYearly,
	isFinops,
	isHappySignal,
	lastYearAvgData,
	hasNoFormat,
	relevantYear = new Date().getFullYear(),
	themeMode = 'dark',
	isDataEstateTrend = false,
	isDataEstateQuery = false,
	lineColor,
	title = ''
}: GetChartOptionsProps) => {
	if (isDataEstateTrend) {
		return buildDataEstateTrendOptions({
			chartData,
			selectedByte,
			conversionUnit,
			lastUpdatedDate,
			title,
			lineColor,
		});
	}

	if (!chartData || Object.keys(chartData).length === 0) {
		return { chartOption: null, isDisplayChart: false };
	}

	const isLightTrend = themeMode === 'lightTrend';
	const allDataAsArray = Object.entries(chartData);
	const allMonths = Object.keys(chartData);

	let badgePercentage: any = null;
	let chartTooltip: string | null = null;

	/* -------------------- DATE CALCULATIONS -------------------- */

	const currentDate = moment().format('YYYY-MM');
	const currentDateYear = moment().format('YYYY');
	const firstMonthFromAllMonths = allMonths[0];
	const lastMonthFromForecast = allMonths[allMonths.length - 1];

	const { filterMonth, lastMonthString, currentYear } =
		getPreviousYearMonthMeta();

	const filterDateEntry = Object.entries(chartData).filter(
		(x) => x[0] === filterMonth
	);

	if (filterDateEntry.length) {
		const count = filterDateEntry[0][1] as number;

		const lastMonthDataIndex = `${currentYear}-${lastMonthString}`;
		const lastMonthEntry = Object.entries(chartData).filter(
			(x) => x[0] === lastMonthDataIndex
		);

		if (lastMonthEntry.length) {
			const lastmonthvalue = lastMonthEntry[0][1] as number;
			if (allMonths.length > 12) {
				badgePercentage = getPercentageOfTwoNumbers(
					count,
					lastmonthvalue
				);
			}
		} else {
			const annualMonth = moment(
				firstMonthFromAllMonths,
				'YYYY-MM'
			).format('MMMM YYYY');
			chartTooltip = `Data available from ${annualMonth}`;
		}
	}

	/* -------------------- LINE DATA -------------------- */

	let lineData = allDataAsArray.filter(([date]) => date <= currentDate);

	if (isForeCast || isLightTrend) {
		lineData =
			allMonths.length >= 12
				? allDataAsArray
				: (allDataAsArray.some(([d]) => d > currentDate) ? allDataAsArray : allDataAsArray);
	}

	let currentMonthIndex = getCurrentMonthIndex(
		allMonths,
		currentDate,
		currentDateYear,
		isYearly
	);

	const formattedCurrentDate = moment().format('YYYY-MM-DD');

	const rotateValue = getRotateValue(hasNoFormat);

	/* -------------------- CHART OPTION -------------------- */

	const chartOption: any = {
		title: {
			show: Object.keys(allDataAsArray).length === 0,
			textStyle: {
				color: isLightTrend ? '#718096' : '#bcbcbc',
				fontWeight: 'normal',
				fontSize: 16,
			},
			text: 'No data for the selected filters',
			left: 'center',
			top: 'center',
		},
		backgroundColor: isLightTrend ? 'transparent' : '#053367',
		grid: {
			top: isLightTrend ? '16%' : (isHappySignal ? '10%' : '13%'),
			left: '15%',
			right: '4%',
			bottom: isLightTrend ? '15%' : '20%',
			backgroundColor: isLightTrend ? '#F0F3F8' : 'transparent',
			show: isLightTrend,
			borderWidth: 0,
		},
		graphic: (isLightTrend && !isDataEstateQuery)
			? [
				{
					type: 'text',
					left: '26%',
					top: '5%',
					style: {
						text: 'Current Growth',
						fill: '#718096',
						font: '500 12px Montserrat, sans-serif',
					},
				},
				{
					type: 'text',
					left: '70%',
					top: '5%',
					style: {
						text: 'Forecast',
						fill: '#718096',
						font: '500 12px Montserrat, sans-serif',
					},
				},
			]
			: undefined,
		xAxis: buildXAxis(
			allMonths,
			hasNoFormat,
			chartInterval,
			rotateValue,
			isYearly,
			isLightTrend
		),
		yAxis: buildYAxis(
			selectedByte,
			conversionUnit,
			isDatabrick,
			hasNoFormat,
			isHappySignal,
			isLightTrend
		),
		series: buildSeries({
			isHappySignal,
			relevantYear,
			lastYearAvgData,
			lineData,
			hasNoFormat,
			selectedByte,
			conversionUnit,
			isYearly,
			allMonths,
			currentMonthIndex,
			isFinops,
			lastUpdatedDate,
			formattedCurrentDate,
			isLightTrend,
			lineColor,
			isDataEstateQuery,
		}),
		tooltip: buildTooltip(
			selectedByte,
			conversionUnit,
			hasNoFormat,
			isYearly,
			isLightTrend
		),
	};

	if (isForeCast && !isLightTrend) {
		chartOption.series[0].markArea = {
			data: [
				[
					{ name: 'Forecast', xAxis: currentDate },
					{ xAxis: lastMonthFromForecast },
				],
			],
		};
	}

	return { chartOption, badgePercentage, chartTooltip, isDisplayChart: true };
};

export const getBarChartOptions = (
	reportData: any[], seriesData: {
		is3d?: any; name: string; dataKey: string; color: string; shadow?: string;
	}[], otherOptions: Record<string, any>, isEvent: boolean, dateRange: any, containerWidth: any, isRMCost: boolean) => {
	const {
		isCost = false,
		isPBI = false,
		legendLabel = '',
		isPercentage = false,
		yAxisOptions = {},
		xAxisOptions = {},
		isNewTheme = false,
		colors
	} = otherOptions;

	const newThemeColors = {
		background: colors.widget.background.card,
		axisLabel: colors.widget.chart.labelColorSecondary,
		gridLine: colors.widget.chart.legendTextColor,
		legendText: colors.widget.chart.legendTextColor,
		legendLabel: colors.widget.chart.legendTextColor,
		tooltipBackground: colors.widget.chart.tooltipBg,
		tooltipText: colors.widget.chart.tooltipText,
	} as const;

	let totalCostData: any = {};
	let totalCountPBI: any = {};
	let xAxisArray: string[] = [];
	let xAxisIndexArray: number[] = [];
	let seriesOptions: any[] = [];

	const dataCount = reportData.length;
	const estimatedBarWidth = containerWidth / Math.max(dataCount, 1);
	// Label positioning based on bar count, series count, and container width
	const getLabelStrategy = () => {
		// Count how many series actually have non-zero values (active series)
		let activeSeriesCount = 0;
		seriesData.forEach((series) => {
			let hasValue = false;
			if (reportData.length > 0) {
				reportData.forEach((res: any) => {
					const key = Object.keys(res)[0];
					const val = res[key]?.[series.dataKey];
					if (typeof val === 'number' && val > 0) {
						hasValue = true;
					}
				});
			}
			if (hasValue) {
				activeSeriesCount++;
			}
		});

		const isSingleActiveSeries = activeSeriesCount <= 1;
		// Priority 1: For very few bars (1-5) - ALWAYS show on TOP regardless of container width
		if (isSingleActiveSeries && dataCount <= 5) {
			return { position: 'top', showOnHover: false };
		}
		// Priority 2: Too many bars or too small container - HIDE (show on hover/tooltip only)
		if (dataCount > 14 || estimatedBarWidth < 90) {
			return { position: false, showOnHover: true };
		}
		// For charts with few bars (6-15): ALWAYS show on TOP (outside) - plenty of space
		if (isSingleActiveSeries && dataCount <= 15) {
			return { position: 'top', showOnHover: false };
		}
		if (dataCount <= 15) {
			return { position: 'inside', showOnHover: false };
		}
		// - Single/one active series: show on TOP
		if (isSingleActiveSeries) {
			return { position: 'top', showOnHover: false };
		}

		// Multi-series with many bars: show INSIDE (centered)
		return { position: 'inside', showOnHover: false };
	};

	const labelStrategy = getLabelStrategy();

	// Calculate total bar values for each data point to check bar size
	let barTotals: number[] = [];
	if (reportData.length > 0) {
		reportData.forEach((res: any) => {
			const key = Object.keys(res)[0];
			let barTotal = 0;
			seriesData.forEach((series) => {
				const val = res[key]?.[series.dataKey];
				if (typeof val === 'number' && val > 0) {
					barTotal += val;
				}
			});
			barTotals.push(barTotal);
		});
	}
	// Find max bar total to determine minimum threshold (hide bars below 20% of max)
	const maxBarTotal = Math.max(...barTotals, 0);

	if (reportData.length > 0) {
		reportData.forEach((res: any, i: number) => {
			const key = Object.keys(res)[0];
			xAxisArray.push(key);

			// Check if the key (date) exists in the dateRangeList and get its index
			if (dateRange.includes(moment.utc(key).format('YYYY-MM-DD'))) {
				// Get the index of the reportData
				xAxisIndexArray.push(i);
			}
		});
	}

	seriesData.forEach((series, index) => {
		const dataArray: any[] = reportData.map((res: any, i: number) => {
			const key = Object.keys(res)[0];

			if (isCost && res[key]?.TotalCost !== undefined) {
				totalCostData[key] = res[key].TotalCost;
			}
			if (isPBI && res[key]?.totalUsers !== undefined) {
				totalCountPBI[key] = res[key].totalUsers;
			}
			return res[key][series.dataKey];
		});

		let itemStyle = series.is3d ? {
			normal: {
				color: series.color,
			},
			emphasis: {
				color: series.color, // Highlight color on hover
				shadowBlur: 20,
				shadowColor: series.shadow, // Glow effect
				shadowOffsetX: 10,
				shadowOffsetY: 10,
			},
		} : {
			color: series.color
		}

		const getNumericValue = (params: any) => {
			const itemValue =
				typeof params === 'object' && params !== null && 'value' in params
					? params.value
					: params;
			if (itemValue === undefined || itemValue === null || isNaN(itemValue)) {
				return null;
			}
			const numValue = typeof itemValue === 'number' ? itemValue : parseFloat(itemValue);
			return numValue > 0 ? numValue : null;
		};

		const getFormattedLabel = (numValue: number) => {
			if (isCost) return `$${formatNumberUnits(numValue)}`;
			if (isPBI) return formatNumberPBI(numValue);
			if (isPercentage) return `${formatNumberUnits(numValue)}%`;
			return formatNumberWithCommas(numValue.toFixed(2));
		};

		const getTotalAtIndex = (dataIndex: number) => {
			if (!reportData[dataIndex]) return 0;

			const key = Object.keys(reportData[dataIndex])[0];

			return seriesData.reduce((total, series) => {
				const val = reportData[dataIndex][key]?.[series.dataKey];
				return typeof val === 'number' && val > 0 ? total + val : total;
			}, 0);
		};

		const shouldHideLabel = (dataIndex: number, numValue: number) => {
			if (labelStrategy.position !== 'inside') {
				const yAxisMax = getAxisDisplayMax(maxBarTotal);
				return barTotals[dataIndex] >= yAxisMax * 0.97;
			}
			const totalAtIndex = getTotalAtIndex(dataIndex);

			// % of current stacked bar
			const segmentPercentage = totalAtIndex > 0 ? (numValue / totalAtIndex) * 100 : 0;

			// % of tallest bar in chart
			const relativeToMaxBar = maxBarTotal > 0 ? (numValue / maxBarTotal) * 100 : 0;

			return segmentPercentage < 12 || relativeToMaxBar < 5;
		};



		seriesOptions.push({
			silent: isRMCost ? !isEvent : isEvent,
			animation: series.is3d ? true : false,
			name: series.name,
			type: 'bar',
			stack: 'total',
			barWidth: '60%', // Make bars thinner - default is usually 80-90%
			data: dataArray,
			itemStyle,
			...(isRMCost ? {
				label: {
					show: labelStrategy.position !== false,
					position: labelStrategy.position || 'inside',
					offset: [0, 0],
					color: '#fff',
					fontSize: estimatedBarWidth < 130 ? 9 : 11,
					formatter: (params: any) => {
						const numValue = getNumericValue(params);
						if (numValue === null) return '';
						const dataIndex = params?.dataIndex;
						if (
							dataIndex !== undefined &&
							dataIndex !== null &&
							shouldHideLabel(dataIndex, numValue)
						) {
							return '';
						}
						return getFormattedLabel(numValue);
					},
				}
			} : {}),
			emphasis: {
				scale: series.is3d ? true : false, // Scale effect on hover
				...(isRMCost ? { focus: 'series' } : {})
			},
			large: true,
			...(labelStrategy.position === 'inside' && {
				labelLayout: {
					hideOverlap: true,  // Smart positioning to avoid overlaps
					moveOverlapBy: 'shiftY'  // Move labels vertically to prevent overlap
				}
			}),
			markArea: series.is3d ? {
				silent: true,

				itemStyle: {
					color: "rgba(255, 255, 255, 0.1)", // Semi-transparent overlay color
				},
				label: {
					show: true,
					position: "insideTop", // Ensures label is fully visible at the top of the markArea
					color: "#ffffff",
					fontSize: 14,
					align: "center",
				},
				data: [
					[
						{
							name: 'Incident Duration',
							xAxis: xAxisIndexArray[0], // Start shading after the first bar (index 1)
						},
						{
							xAxis: xAxisIndexArray[xAxisIndexArray.length - 1], // End shading before the last two bars
						},
					],
				],
			} : {},

		});
	});

	const option: any = {
		backgroundColor: isNewTheme ? newThemeColors?.background : '#003771',
		grid: {
			top: '4%',
			left: '3%',
			right: '3%',
			bottom: isRMCost ? '4%' : '25%',
			containLabel: true
		},
		xAxis: {
			type: 'category',
			data: xAxisArray,
			axisTick: { show: isRMCost ? false : true },
			splitLine: {
				show: false // Remove vertical grid lines
			},
			axisLabel: {

				interval: 'auto', // Automatically adjusts based on space
				color: isNewTheme ? newThemeColors?.axisLabel : '#fff',
				hideOverlap: true, // Automatically hide overlapping labels
				margin: 12,
				formatter: function (value: string) {
					const parts = value.split(" "); // Split by space
					return parts.length > 1 ? parts[0] + "\n" + parts[1] : parts[0];
				}
			},
			...xAxisOptions,
		},
		yAxis: {
			type: 'value',
			splitLine: {
				show: true,
				lineStyle: {
					color: isNewTheme ? newThemeColors?.gridLine : '#374151', // Keep horizontal grid lines but with subtle color
					type: 'solid'
				}
			},
			...(isPercentage && {
				min: 0,
				max: 100,
				interval: 50,
			}),
			axisLabel: {
				color: isNewTheme ? newThemeColors?.axisLabel : '#fff',
				formatter: (value: number) => {
					if (isPBI) {
						if (value >= 1000)
							return formatNumberPBI(value);
						else
							return formatNumberWithCommas(value.toFixed(2));
					} else if (isCost) {
						return `$${formatNumberUnits(value)}`;
					} else if (isPercentage) {
						return `${formatNumberUnits(value)}%`;
					} else {
						return formatNumberWithCommas(value.toFixed(2));
					}
				}
			},
			...yAxisOptions,
		},
		series: seriesOptions,

		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'shadow'
			},
			position: function (point: any, params: any, dom: any, rect: any, size: any) {
				const [x, y] = point;
				const [contentWidth, contentHeight] = size.contentSize;
				const [viewWidth, viewHeight] = size.viewSize;

				const xOffset = 10;
				const yOffset = 10;

				let posX = x + xOffset;
				let posY = y - contentHeight - yOffset;

				// Ensure the tooltip stays within the viewport horizontally
				if (posX + contentWidth > viewWidth) {
					// If tooltip goes beyond the right edge, move it to the left
					posX = x - contentWidth - xOffset;
				}

				if (posX < 0) {
					// If tooltip goes beyond the left edge, place it on the right of the cursor
					posX = x + xOffset;
				}

				// Ensure the tooltip stays within the viewport vertically
				if (posY < 0) {
					// If tooltip goes beyond the top, position it below the cursor
					posY = y + yOffset;
				}

				// Ensure tooltip doesn’t go beyond the bottom of the viewport
				if (posY + contentHeight > viewHeight) {
					posY = viewHeight - contentHeight - yOffset;
				}

				return [posX, posY];
			},
			backgroundColor: isNewTheme ? newThemeColors?.tooltipBackground : '#003771',
			textStyle: {
				fontFamily: 'Montserrat',
				fontSize: 14,
				color: isNewTheme ? newThemeColors?.tooltipText : '#A7ADCB'
			},
			formatter: (params: any[]) => {
				let tooltipContent = `${params[0].axisValue}<br/>`;
				if (isCost && totalCostData[params[0].axisValue] !== undefined) {
					const value = totalCostData[params[0].axisValue] || 0;
					tooltipContent += `TotalCost: $${formatNumberUnits(value)}<br/></div>`;
				}
				if (isPBI && totalCountPBI[params[0].axisValue] !== undefined) {
					const value = totalCountPBI[params[0].axisValue] || 0;
					tooltipContent += `Total: ${formatNumberWithCommas(value.toFixed(2))}<br/></div>`;
				}
				params.forEach((item) => {
					const itemValue = item.value !== undefined ? item.value : 0;
					const displayName = item.seriesName;
					if (isCost) {
						tooltipContent += `${item.marker} ${displayName}: $${formatNumberUnits(itemValue)} <br/>`;
					} else if (isPercentage) {
						tooltipContent += `${item.marker} ${displayName}: ${formatNumberUnits(itemValue)}% <br/>`;
					} else {
						tooltipContent += `${item.marker} ${displayName}: ${formatNumberWithCommas((itemValue).toFixed(2))} <br/>`;
					}
				});
				return tooltipContent;
			}
		},
		graphic: {
			elements: [
				{
					type: 'text',
					left: '10px',
					bottom: containerWidth >= 1097 ? '10px' : '20px',
					style: {
						text: legendLabel,
						fill: isNewTheme ? newThemeColors?.legendLabel : '#F5F5F5',
						fontFamily: "Montserrat",
						fontWeight: 'bold',
					}
				}
			]
		},
		legend: {
			show: isRMCost ? false : true,
			left: '80px',
			bottom: '5px',
			formatter: function (name: any) {
				return name;
			},
			textStyle: {
				color: isNewTheme ? newThemeColors?.legendText : '#fff',
				fontSize: containerWidth < 770 ? 10 : 12
			},
			itemGap: 10,
			selectedMode: false // Disable selection mode for legend items
		}
	};

	// Only add dataZoom if isCost is false
	if (!isCost) {
		option.dataZoom = [
			{
				type: 'slider', // Adds a draggable scrollbar
				show: true,
				start: 0,
				end: reportData.length > 100 ? 20 : 100,
				xAxisIndex: 0,
				bottom: '18%',
				height: 18,
				backgroundColor: 'rgba(255,255,255,0.1)',
				dataBackground: {
					lineStyle: {
						color: '#A7ADCB'
					},
					areaStyle: {
						color: '#A7ADCB'
					}
				},
				textStyle: {
					color: '#fff'
				},
				showDetail: false,  // Hide the tooltip when scrolling
				labelFormatter: () => '' // Hide the start & end values
			},
			{
				type: 'inside',
				xAxisIndex: 0
			}
		];
	}

	return option;
};

export const getDoughnutChartOptions = (
	totalSum: any,
	seriesData: { name: string, value: any, percentage: string, formattedName: string }[], isDataLabel: boolean, isLegendLabel: boolean, timeFormat?: boolean, title?: any
) => {
	const titleValue = typeof title === "object" && title?.props?.text;
	const noDollarSymbol = ["Major INC Details", "Outages"].includes(titleValue || "");
	// Dynamically calculate font size based on screen width
	const getResponsiveFontSize = () => {
		const screenWidth = window.innerWidth;
		if (screenWidth < 600) {
			return '12px';
		} else if (screenWidth < 1024) {
			return '16px';
		} else {
			return '20px';
		}
	};

	const option = {
		backgroundColor: 'transparent',
		tooltip: {
			backgroundColor: '#003771',
			textStyle: {
				color: '#A9ADCB',
			},
			formatter: function (params: any) {
				let value = params.value;
				if (timeFormat) {
					const { Days, Hours, Minutes } = convertDecimalToTime(value);
					return `${params.name} : ${params.percent}% (${Days > 0 ? Days + 'Day ' : ''}${Hours > 0 ? Hours + 'Hrs ' : ''}${Minutes > 0 ? Minutes + 'Min' : ''})`;
				}
				let formattedValue = formatNumberByUnits(value);
				const valueText = noDollarSymbol
					? formattedValue
					: '$' + formattedValue;
				return `${params.name} : ${params.percent}% (${valueText})`;
			},
			position: 'inside'
		},
		legend: {
			type: "scroll",
			// Scroll arrow icons
			pageIconColor: '#A7ADCB',            // arrow color (default)
			pageIconInactiveColor: '#444C63',    // when arrows are disabled
			pageTextStyle: {
				color: '#1B3555',                   // optional page number style
				fontSize: 10
			},
			pageButtonItemGap: 5,                // gap between legend and scroll arrows
			pageButtonGap: 10,
			show: isLegendLabel,
			orient: 'horizontal',
			bottom: '2%',
			left: '2%',
			textStyle: {
				color: '#000'
			},
			data: seriesData.map((item) => item.name),
			selectedMode: false
		},
		series: [
			{
				type: 'pie',
				radius: ['70%', '50%'],
				center: ['50%', '50%'],
				label: {
					show: isDataLabel,
					position: 'inside',
					formatter: function (params: any) {
						let labelValue = '';
						if (timeFormat) {
							const { Days, Hours, Minutes } = convertDecimalToTime(params.value);
							labelValue = `${Days > 0 ? Days + 'd ' : ''}${Hours > 0 ? Hours + 'h ' : ''}${Minutes > 0 ? Minutes + 'm' : ''}`;
						} else {
							labelValue = noDollarSymbol ? params.value.toString() : `$${params.value}`;
						}
						if (labelValue.length > 5) {
							labelValue = labelValue.substring(0, 5) + '...';
						}
						return `{value|${labelValue}}`
					},
					rich: {
						value: {
							fontSize: 14,
							color: '#fff',
							lineHeight: 18,
							fontWeight: 'bold'
						}
					}
				},
				data: seriesData.map((item: any) => ({
					name: item.name,
					value: item.value,
					itemStyle: {
						color: item.color
					}
				})),
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: 'rgba(0, 0, 0, 0.5)'
					}
				},
				labelLine: {
					show: false
				},

			}
		],
		graphic: {
			type: 'text',
			left: 'center',
			top: 'center',
			bottom: 'center',
			right: 'center',
			style: {
				text: totalSum,
				textAlign: 'center',
				// fill: '#000',
				rich: {
					small: {
						fontSize: 12,
						fontWeight: 'normal',
						lineHeight: 20,
						fill: '#000'
					},
					large: {
						fontSize: getResponsiveFontSize(),
						fontStyle: 'normal',
						fontWeight: 700,
						lineHeight: 29,
						fontfamily: 'Montserrat',
						color: "#000000",
						fill: '#000000',
						display: "flex",
						alignitems: "flex-end"
					},
					subRed: {
						fontSize: 12,
						fontWeight: 'normal',
						lineHeight: 20,
						fill: '#FE2626',
					},
					subGreen: {
						fontSize: 12,
						fontWeight: 'normal',
						lineHeight: 20,
						fill: '#01c008',
					},
				},
			}
		},
		textStyle: {
			fontFamily: "Montserrat"
		}
	};

	return option;
};

export const getPieChartOptions = (
	seriesData: any,
	containerWidth: any,
	colorMapping: any,
	showDollar: boolean,
	radius?: any,
	customLegendPosition?: boolean,
	customChartPosition?: any
) => {
	const option = {
		tooltip: {
			backgroundColor: '#003771',
			textStyle: {
				color: '#A9ADCB'
			},
			formatter: function (params: any) {
				let value = params.value;
				let formattedValue = formatNumberByUnits(value);
				const displayValue = showDollar ? `$${formattedValue}` : formattedValue;
				return `${params.name} : ${params.percent}%  (${displayValue})`;
			},
			position: 'inside'
		},
		legend: {
			// top: '70%',
			type: 'scroll',
			// Scroll arrow icons
			pageIconColor: '#A7ADCB',
			pageIconInactiveColor: '#444C63',
			pageTextStyle: {
				color: '#A7ADCB',
				fontSize: 10
			},
			pageButtonItemGap: 5,
			pageButtonGap: 10,
			top: customLegendPosition ? '63%' : 'auto',
			bottom: customLegendPosition ? 'auto' : 4,
			left: customLegendPosition ? '4%' : 'center',
			data: seriesData.map((item: any) => item && item.name ? item.name : ''),
			textStyle: {
				color: '#A7ADCB',
				fontSize: containerWidth < 370 ? 10 : 12,
				fontWeight: 500
			}
		},
		series: [
			{
				type: 'pie',
				radius: radius ? radius : '70%',
				center: customChartPosition ? customChartPosition : ['50%', '45%'],
				selectedMode: 'single',
				label: {
					show: true,
					position: 'inside',
					fontSize: 14,
					textStyle: {
						align: 'center',
						verticalAlign: 'middle'
					},
					formatter: function (params: any) {
						return `${params.percent.toFixed(2)}%`;
					},
					rich: {
						a: {
							color: '#E5E5E5',
							fontSize: 12,
							lineHeight: 20
						},
						b: {
							color: '#E5E5E5',
							fontSize: 12
						}
					},
					color: '#F5F5F5'
				},
				data: seriesData.map((item: any) => ({
					name: item.name,
					value: item.value,
					itemStyle: colorMapping?.[item.name] ? { color: colorMapping[item.name] } : undefined,
					label: {
						formatter: function (params: any) {
							if (params.percent < 8) {
								return ''; // Hide labels for small percentages
							}
							return `${item.formattedName}`;
						}
					}
				})),
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: 'rgba(0, 0, 0, 0.5)'
					}
				}
			}
		],
		textStyle: {
			fontFamily: 'Montserrat'
		}
	};

	return option;
};

interface PieChartOptionsConfig {
	theme: Theme;
	seriesData: any;
	containerWidth: any;
	colorMapping: any;
	showDollar: boolean;
	showPercentage: boolean;
	radius?: any;
	hasExpand?: boolean;
	customLegendPosition?: boolean;
	customChartPosition?: any;
}
export const getPieChartOptionsNew = ({
	theme,
	seriesData,
	containerWidth,
	colorMapping = {},
	showDollar,
	showPercentage,
	radius,
	hasExpand,
	customLegendPosition,
	customChartPosition
}: PieChartOptionsConfig) => {
	const colors = theme.palette;
	const option = {
		tooltip: {
			backgroundColor: colors.widget.chart.tooltipBg,
			textStyle: {
				color: colors.widget.chart.tooltipText,
			},
			formatter: function (params: any) {
				let value = params.value;
				let formattedValue = formatNumberByUnits(value);
				const displayValue = showDollar ? `$${formattedValue}` : formattedValue;
				return `${params.name} : ${params.percent}%  (${displayValue})`;
			},
			position: 'inside'
		},
		legend: {
			type: 'scroll',

			icon: 'roundRect',
			itemWidth: 14,
			itemHeight: 14,

			pageIconColor: colors.widget.chart.legendActiveUpDownIconColor,
			pageIconInactiveColor: colors.widget.chart.legendInactiveUpDownIconColor,
			pageTextStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: 10
			},
			pageButtonItemGap: 5,
			pageButtonGap: 10,

			data: seriesData.map((item: any) => item?.name ?? ''),

			textStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: containerWidth < 370 ? 10 : 12,
				fontWeight: 500,
			},

			// expand vs non-expand positioning
			...(hasExpand
				? {
					orient: 'vertical',
					right: customLegendPosition ? '4%' : '6%',
					top: customLegendPosition ? '10%' : 'middle',
				}
				: {
					orient: 'horizontal',
					left: 0,
					bottom: 0,
					itemGap: 16,
				}),
		},
		series: [
			{
				type: 'pie',
				radius: radius ? radius : '90%',
				center: customChartPosition ? customChartPosition : ['30%', '52%'],
				selectedMode: 'single',
				label: {
					show: true,
					position: 'inside',
					fontSize: 14,
					fontWeight: 600,
					textStyle: {
						align: 'center',
						verticalAlign: 'middle'
					},
					formatter: function (params: any) {
						if (showPercentage) {
							return `${params.percent.toFixed(0)}%`;
						}
						return '';
					},
					rich: {
						a: {
							color: colors.widget.text.value,
							fontSize: 12,
							lineHeight: 20
						},
						b: {
							color: colors.widget.text.value,
							fontSize: 12
						}
					},
					color: colors.widget.text.value,
				},
				data: seriesData.map((item: any) => ({
					name: item.name,
					value: item.value,
					itemStyle: colorMapping?.[item.name] ? { color: colorMapping[item.name] } : undefined,
					label: showPercentage
						? {
							formatter: function (params: any) {
								if (params.percent < 8) return '';
								return `${params.percent.toFixed(0)}%`;
							}
						}
						: {
							formatter: function (params: any) {
								if (params.percent < 8) return '';
								return `${item.formattedName}`;
							}
						}
				})),
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: 'rgba(0, 0, 0, 0.5)'
					}
				}
			}
		],
		textStyle: {
			fontFamily: 'Montserrat'
		}
	};

	return option;
};

export const getDoughnutChartWidgetOptions = (
	theme: Theme,
	totalSum: any,
	seriesData: { name: string; value: any; percentage: string; formattedName: string }[],
	isDataLabel: boolean,
	isLegendLabel: boolean,
	timeFormat?: boolean,
	isCost?: boolean,
) => {
	const colors = theme.palette;

	const getResponsiveFontSize = () => {
		const screenWidth = window.innerWidth;
		if (screenWidth < 600) return '14px';
		if (screenWidth < 1024) return '18px';
		return '20px';
	};

	const option = {
		backgroundColor: colors.widget.background.chart,

		tooltip: {
			backgroundColor: colors.widget.chart.tooltipBg,
			textStyle: {
				color: colors.widget.chart.tooltipText,
			},
			formatter: function (params: any) {
				let value = params.value;
				if (timeFormat) {
					const { Days, Hours, Minutes } = convertDecimalToTime(value);
					return `${params.name} : ${params.percent}% (${Days > 0 ? Days + 'Day ' : ''}${Hours > 0 ? Hours + 'Hrs ' : ''}${Minutes > 0 ? Minutes + 'Min' : ''})`;
				}
				let formattedValue = formatNumberByUnits(value);
				const valueText = !isCost
					? formattedValue
					: '$' + formattedValue;
				return `${params.name} : ${params.percent}% (${valueText})`;
			},
			position: 'inside',
		},

		legend: {
			type: 'scroll',
			pageIconColor: colors.widget.chart.legendActiveUpDownIconColor,
			pageIconInactiveColor: colors.widget.chart.legendInactiveUpDownIconColor,
			pageTextStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: 10,
			},
			pageButtonItemGap: 5,
			pageButtonGap: 10,
			show: isLegendLabel,
			orient: 'horizontal',
			bottom: 0,
			left: 0,
			textStyle: {
				color: colors.widget.chart.legendTextColor,
			},
			data: seriesData.map((item) => item.name),
			selectedMode: false,
		},

		series: [
			{
				type: 'pie',
				radius: ['80%', '60%'],
				center: ['50%', '50%'],
				label: {
					show: isDataLabel,
					position: 'inside',
					formatter: function (params: any) {
						let labelValue = '';
						if (timeFormat) {
							const { Days, Hours, Minutes } = convertDecimalToTime(params.value);
							labelValue = `${Days > 0 ? Days + 'd ' : ''}${Hours > 0 ? Hours + 'h ' : ''}${Minutes > 0 ? Minutes + 'm' : ''}`;
						} else {
							labelValue = !isCost ? params.value.toString() : `$${params.value}`;
						}
						if (labelValue.length > 5) {
							labelValue = labelValue.substring(0, 5) + '...';
						}
						return `{value|${labelValue}}`;
					},
					rich: {
						value: {
							fontSize: 14,
							color: colors.widget.chart.labelColor,
							lineHeight: 18,
							fontWeight: 'bold',
						},
					},
				},

				data: seriesData.map((item: any) => ({
					name: item.name,
					value: item.value,
					itemStyle: {
						color: item?.color,
					},
				})),

				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: 'rgba(0, 0, 0, 0.5)',
					},
				},

				labelLine: {
					show: false,
				},
			},
		],

		graphic: {
			type: 'text',
			left: 'center',
			top: 'center',
			bottom: 'center',
			right: 'center',
			style: {
				text: totalSum,
				textAlign: 'center',
				rich: {
					small: {
						fontSize: 14,
						fontWeight: 'normal',
						lineHeight: 20,
						fill: colors.widget.text.title,
					},
					large: {
						fontSize: getResponsiveFontSize(),
						fontStyle: 'normal',
						fontWeight: 700,
						lineHeight: 29,
						fontfamily: 'Montserrat',
						color: colors.widget.text.title,
						fill: colors.widget.text.title,
						display: "flex",
						alignitems: "flex-end"
					},
					subRed: {
						fontSize: 14,
						fontWeight: 'normal',
						lineHeight: 20,
						fill: '#FE2626',
					},
					subGreen: {
						fontSize: 14,
						fontWeight: 'normal',
						lineHeight: 20,
						fill: '#01c008',
					},
				},
			},
		},

		textStyle: {
			fontFamily: 'Montserrat',
		},
	};

	return option;
};

export const getPieChartWidgetOptions = (
	theme: Theme,
	seriesData: {
		name: string;
		value: any;
		percentage: string;
		formattedName: string;
		color?: string;
	}[],
	isDataLabel: boolean,
	isLegendLabel: boolean,
	isCost?: boolean,
) => {
	const colors = theme.palette;

	const option = {
		backgroundColor: colors.widget.background.chart,

		tooltip: {
			backgroundColor: colors.widget.chart.tooltipBg,
			textStyle: {
				color: colors.widget.chart.tooltipText,
			},
			formatter: (params: any) => {
				const value = params.value;
				const formattedValue = formatNumberByUnits(value);
				const valueText = !isCost
					? formattedValue
					: '$' + formattedValue;
				return `${params.name} : ${params.percent}% (${valueText})`;
			},
		},

		legend: {
			type: 'scroll',
			show: isLegendLabel,
			orient: 'horizontal',
			bottom: 0,
			left: 0,
			textStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: 10,
			},
			data: seriesData.map((item) => item.name),
			selectedMode: false,
		},

		series: [
			{
				type: 'pie',
				radius: '80%',
				center: ['50%', '50%'],

				label: {
					show: isDataLabel,
					position: 'inside',
					formatter: (params: any) => {
						const value = Number(params.value ?? 0);

						const formattedValue = new Intl.NumberFormat('en-US', {
							notation: 'compact',
							compactDisplay: 'short',
							maximumFractionDigits: 2,
						}).format(value);

						return isCost ? `$${formattedValue}` : formattedValue;
					},
					color: '#FFFFFF',
					fontWeight: 500,
					fontSize: 14,
				},

				data: seriesData.map((item: any) => ({
					name: item.name,
					value: item.value,
					itemStyle: {
						color: item.color,
					},
				})),

				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: 'rgba(0,0,0,0.3)',
					},
				},

				labelLine: {
					show: false,
				},
			},
		],

		textStyle: {
			fontFamily: 'Montserrat',
		},
	};

	return option;
};


type ComboSeries = {
	key: string; // field in data
	name: string; // legend label
	type: "bar" | "line";
	color?: string;
	yAxis?: "left" | "right"; // default: bar->left, line->right
	smooth?: boolean; // line only
	symbolSize?: number; // line only
};

export const buildBarLineComboOptions = ({
	data,
	colors,
	showGridLines = false,
	isCost = false,
	xKey = "name",
	xAxisLabel = "Month",
	xLabelRotate = 0,
	xLabelTruncate = 8,
	series = [] as ComboSeries[],
	tooltipFormatter,
	rightAxisType = "percent" as "percent" | "dollar" | "number",
}: any) => {
	if (!data?.length || !series?.length) return null;

	const labels = data.map((d: any) => d?.[xKey]);

	const leftAxisVals: number[] = [];
	const rightAxisVals: number[] = [];

	series.forEach((s: ComboSeries) => {
		const axis = s.yAxis || (s.type === "bar" ? "left" : "right");
		data.forEach((d: any) => {
			const v = d?.[s.key];
			if (typeof v === "number") {
				if (axis === "left") leftAxisVals.push(v);
				if (axis === "right") rightAxisVals.push(v);
			}
		});
	});

	const leftMinData = leftAxisVals.length ? Math.min(...leftAxisVals) : 0;
	const leftMaxData = leftAxisVals.length ? Math.max(...leftAxisVals) : 0;

	const rightMinData = rightAxisVals.length ? Math.min(...rightAxisVals) : 0;
	const rightMaxData = rightAxisVals.length ? Math.max(...rightAxisVals) : 0;

	const pad = (max: number) => (max === 0 ? 1 : max * 1.1);

	const getIntervalByRule = (max: number) => {
		if (!Number.isFinite(max) || max <= 0) return 1;
		if (max < 1_000) return 100;
		if (max < 10_000) return 500;
		if (max < 50_000) return 1_000;
		if (max < 200_000) return 5_000;
		if (max < 1_000_000) return 10_000;
		return 50_000;
	};

	const leftMin = leftMinData < 0 ? leftMinData : 0;
	const leftMax = pad(leftMaxData);
	const leftInterval = getIntervalByRule(leftMax);

	const formatCost = (v: number) => {
		if (!Number.isFinite(v)) return "";
		return `$${Math.round(v).toLocaleString()}`;
	};

	const formatNumber = (v: number) => {
		if (!Number.isFinite(v)) return "";
		return `${v}`;
	};

	const formatPercent = (v: number) => (Number.isFinite(v) ? `${v}%` : "");

	const axisLeftFormatter = (v: number) =>
		isCost ? formatCost(v) : formatNumber(v);

	const rightAxisConfig = (() => {
		if (rightAxisType === "percent") {
			return {
				min: 0,
				max: 100,
				interval: 25,
				formatter: formatPercent,
			};
		}

		const min = rightMinData < 0 ? rightMinData : 0;
		const max = pad(rightMaxData);
		const interval = getIntervalByRule(max);

		return {
			min,
			max,
			interval,
			formatter: rightAxisType === "dollar" ? formatCost : formatNumber,
		};
	})();

	const getAxisName = (axisSide: "left" | "right") => {
		const names = (series as ComboSeries[])
			.filter((s) => (s.yAxis || (s.type === "bar" ? "left" : "right")) === axisSide)
			.map((s) => s.name);

		return Array.from(new Set(names)).join(", ");
	};

	const leftAxisName = getAxisName("left");
	const rightAxisName = getAxisName("right");




	const formatTooltipValue = (value: any, axis: "left" | "right") => {
		if (typeof value !== "number") return value;
		return axis === "left"
			? axisLeftFormatter(value)
			: rightAxisConfig.formatter(value);
	};

	const seriesOptions = (series || []).map((s: ComboSeries) => {
		const axis = s.yAxis || (s.type === "bar" ? "left" : "right");
		const yAxisIndex = axis === "left" ? 0 : 1;

		if (s.type === "bar") {
			return {
				name: s.name,
				type: "bar",
				yAxisIndex,
				data: data.map((d: any) => d?.[s.key]),
				z: 1,
				label: { show: true, position: "top", color: colors.widget.text.main },
				itemStyle: { color: s.color || colors.widget.colors.color2 },
			};
		}

		return {
			name: s.name,
			type: "line",
			yAxisIndex,
			data: data.map((d: any) => d?.[s.key]),
			smooth: s.smooth ?? true,
			symbol: "circle",
			symbolSize: s.symbolSize ?? 12,
			lineStyle: { width: 2, color: s.color },
			itemStyle: { color: s.color },
			z: 3,
		};
	});

	return {
		backgroundColor: "transparent",

		legend: {
			show: true,
			top: 0,
			left: 0,
			textStyle: { color: colors.widget.chart.labelColorSecondary },
		},

		tooltip: {
			trigger: "axis",
			axisPointer: { type: "line" },
			formatter:
				tooltipFormatter ||
				((params: any[]) => {
					const title = params?.[0]?.axisValueLabel ?? params?.[0]?.name ?? "";
					const rows = params
						.map((p) => {
							const cfg = series.find((s: ComboSeries) => s.name === p.seriesName);
							const axis = cfg?.yAxis || (cfg?.type === "bar" ? "left" : "right");
							const val = formatTooltipValue(p.value, axis);
							return `${p.marker} <strong>${p.seriesName}</strong>: <strong>${val}</strong>`;
						})
						.join("<br/>");
					return `${title}<br/>${rows}`;
				}),
		},

		grid: {
			left: "8%",
			right: "10%",
			top: "14%",
			bottom: "10%",
			containLabel: true,
		},

		xAxis: {
			type: "category",
			data: labels,
			name: xAxisLabel || undefined,
			nameLocation: "middle",
			nameGap: xAxisLabel ? 35 : 20,
			axisTick: { show: false },
			axisLabel: {
				rotate: xLabelRotate,
				color: colors.widget.chart.labelColorSecondary,
				formatter: (value: any) => {
					const v = String(value ?? "");
					return v.length > xLabelTruncate ? v.substring(0, xLabelTruncate) + "…" : v;
				},
			},
			nameTextStyle: { color: colors.widget.chart.labelColorSecondary, fontWeight: 500 },
		},

		yAxis: [
			{
				type: "value",
				name: leftAxisName || undefined,
				nameLocation: "middle",
				nameRotate: 90,
				nameGap: 45,
				nameTextStyle: {
					color: colors.widget.chart.labelColorSecondary,
				},
				min: leftMin,
				max: leftMax,
				interval: leftInterval,
				splitLine: { show: showGridLines },
				axisLabel: {
					color: colors.widget.chart.labelColorSecondary,
					formatter: (v: number) => axisLeftFormatter(v),
				},
				axisLine: { show: true, lineStyle: { type: "dotted", width: 1 } },
			},
			{
				type: "value",
				name: rightAxisName || undefined,
				nameLocation: "middle",
				nameRotate: 270,
				nameGap: 55,
				nameTextStyle: {
					color: colors.widget.chart.labelColorSecondary,
				},
				min: rightAxisConfig.min,
				max: rightAxisConfig.max,
				interval: rightAxisConfig.interval,
				splitLine: { show: false },
				axisLabel: {
					color: colors.widget.chart.labelColorSecondary,
					formatter: (v: number) => rightAxisConfig.formatter(v),
				},
				axisLine: { show: true, lineStyle: { type: "dotted", width: 1 } },
			},
		],

		series: seriesOptions,
	};
};

// Gauge Chart Options
export const buildGaugeOptions = (
	value: number,
	colors: any,
) => {
	const safeValue = Math.min(Math.max(value, 0), 100);

	return {
		backgroundColor: 'transparent',
		series: [
			{
				type: 'gauge',
				center: ['50%', '55%'],
				radius: '80%',
				min: 0,
				max: 100,
				emphasis: { disabled: true },
				pointer: { show: false },
				progress: { show: false },
				axisLine: {
					show: true,
					lineStyle: {
						width: 20, // 👈 consistent width
						color: [[1, colors.widget.gaugeChart.unfilledArcColor]],
					},
				},
				axisTick: { show: false },
				splitLine: { show: false },
				axisLabel: { show: false },
			},
			{
				type: 'gauge',
				center: ['50%', '55%'],
				radius: '80%',
				min: 0,
				max: 100,
				emphasis: { disabled: true },
				pointer: { show: false },
				axisLine: { show: false },
				progress: {
					show: true,
					width: 20,
					roundCap: false,
					itemStyle: {
						color: colors.widget.gaugeChart.filledArcColor,
					},
				},
				axisTick: { show: false },
				splitLine: { show: false },
				axisLabel: { show: false },
				detail: {
					valueAnimation: true,
					offsetCenter: [0, 10],
					fontSize: 32,
					fontWeight: 'normal',
					formatter: `${safeValue.toFixed(2)}%`,
					color: colors.widget.text.value,
				},
				data: [{ value: safeValue }],
			},
			{
				type: 'gauge',
				center: ['50%', '55%'],
				radius: '80%',
				min: 0,
				max: 100,
				emphasis: { disabled: true },
				pointer: { show: false },
				axisLine: { show: false },
				progress: { show: false },
				axisTick: { show: false },
				splitLine: { show: false },
				axisLabel: { show: false },
				detail: {
					offsetCenter: [0, '-22%'],
					fontSize: 18,
					fontWeight: 'normal',
					formatter: 'Uptime',
					color: colors.widget.text.value,
				},
				data: [{ value: safeValue }],
			},
		],
	};
};


// Multi Line Chart Options
const getIntervalByRule = (max: number) => {
	if (max < 1_000) return 500;
	if (max < 10_000) return 200;
	if (max < 50_000) return 10000;
	if (max < 100_000) return 50000;
	return 1000000;
};

const roundUpToInterval = (
	value: number,
	interval: number,
) => Math.ceil(value / interval) * interval;

const getAxisFormatter = (isCost: boolean) => {
	const formatCost = (value: number) =>
		Number.isFinite(value)
			? `$${Math.round(value).toLocaleString()}`
			: '';

	const formatNumber = (value: number) =>
		Number.isFinite(value) ? `${value}` : '';

	return (value: number) =>
		isCost ? formatCost(value) : formatNumber(value);
};

// Formats a data point label as millions with 2 decimals, e.g. 28325867 -> "28.33M"
const getMillionsFormatter = () => (value: number) =>
	Number.isFinite(value)
		? `${(value / 1_000_000).toFixed(2)}M`
		: '';

const getGraphicOptions = (
	lastUpdated: string | undefined,
	colors: any,
) =>
	lastUpdated
		? [
			{
				type: 'text',
				right: '4%',
				top: 10,
				style: {
					text: `Last updated date: ${lastUpdated}`,
					fill:
						colors.widget.chart
							.labelColorSecondary,
					fontSize: 12,
				},
			},
		]
		: [];

const getTooltipFormatter = (
	tooltipFormatter:
		| ((params: any[]) => string)
		| undefined,
	axisFormatter: (value: number) => string,
) =>
	(params: any[]) => {
		const validParams = params.filter((param) => {
			const value = Array.isArray(param.value)
				? param.value.at(-1)
				: param.value;

			return (
				value !== null &&
				value !== undefined &&
				value !== '' &&
				Number.isFinite(Number(value))
			);
		});

		const uniqueParams = validParams.filter(
			(param, index, allParams) =>
				allParams.findIndex(
					(item) =>
						item.seriesName ===
						param.seriesName,
				) === index,
		);

		if (tooltipFormatter) {
			return tooltipFormatter(uniqueParams);
		}

		const title =
			uniqueParams?.[0]?.axisValueLabel ?? '';

		const rows = uniqueParams
			.map((param) => {
				const value = Array.isArray(param.value)
					? param.value.at(-1)
					: param.value;

				return `${param.marker} <strong>${param.seriesName
					}</strong>: <strong>${axisFormatter(
						Number(value),
					)}</strong>`;
			})
			.join('<br/>');

		return `${title}<br/>${rows}`;
	};

const getForecastStartIndex = (
	data: any[],
	xKey: string,
	forecastStartMonth?: string,
) => {
	if (!forecastStartMonth) {
		return -1;
	}

	return data.findIndex(
		(item) => item?.[xKey] === forecastStartMonth,
	);
};

const createActualSeriesData = (
	data: any[],
	key: string,
	forecastStartIndex: number,
) =>
	data.map((dataItem: any, index: number) => {
		if (
			forecastStartIndex !== -1 &&
			index >= forecastStartIndex
		) {
			return null;
		}

		return dataItem?.[key];
	});

const createForecastSeriesData = (
	data: any[],
	key: string,
	forecastStartIndex: number,
) =>
	data.map((dataItem: any, index: number) => {
		if (forecastStartIndex === -1) {
			return null;
		}

		if (index === forecastStartIndex - 1) {
			return dataItem?.[key];
		}

		if (index >= forecastStartIndex) {
			return dataItem?.[key];
		}

		return null;
	});

export const getDynamicLineColor = (
	index: number,
	totalColors: number,
) => {
	const safeTotalColors = Math.max(totalColors, 1);

	const hue =
		(index * 360) / safeTotalColors;

	const saturation =
		65 + (index % 4) * 8;

	const lightness =
		38 + (index % 3) * 5;

	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const getSeriesTotal = (
	data: any[],
	key: string,
) =>
	data.reduce((total, dataItem) => {
		const value = Number(dataItem?.[key]);

		return Number.isFinite(value)
			? total + value
			: total;
	}, 0);

const createLineSeriesOption = (
	item: any,
	seriesData: any[],
	lineType: 'solid' | 'dotted',
	color: string,
	showTotal: boolean,
	total: number,
	axisFormatter: (value: number) => string,
	showEndTotal: boolean,
) => ({
	name: item.name,
	type: 'line',
	data: seriesData,
	smooth: item.smooth ?? false,
	symbol: 'circle',
	symbolSize: item.symbolSize ?? 10,
	connectNulls: false,
	lineStyle: {
		width: 2,
		type: lineType,
		color,
	},
	itemStyle: {
		color,
	},
	// One data label per point (not just at the line's end), showing that
	// point's own value via axisFormatter (e.g. millions formatting).
	label: showTotal
		? {
			show: true,
			position: 'top',
			color,
			fontWeight: 500,
			formatter: (params: any) =>
				params.value == null
					? ''
					: axisFormatter(Number(params.value)),
		}
		: {
			show: false,
		},
	// A single bold label at the series' last point showing the running
	// total across all points — visually distinct from the per-point
	// `label` above (larger, bolder) so the two are never confused, per the
	// TL's ask to differentiate an end total from per-point values. Given
	// extra `distance` from the line's terminus so it doesn't crowd the
	// last point's own per-point label when both render together; the
	// chart's `grid.right` reserves enough margin for this label to spill
	// past the plot area rather than being clipped.
	endLabel: showEndTotal
		? {
			show: true,
			formatter: () => axisFormatter(total),
			color,
			fontWeight: 700,
			fontSize: 14,
			distance: 20,
		}
		: {
			show: false,
		},
	// endLabel is a separate ECharts primitive from `label` — it's
	// positioned via its own `distance` above, not through labelLayout's
	// overlap engine, so it's intentionally left out of this gate.
	labelLayout: showTotal
		? {
			moveOverlap: 'shiftY',
			hideOverlap: true,
		}
		: undefined,
	emphasis: {
		focus: 'series',
	},
	z: 3,
});

const getSeriesOptions = (
	series: any[],
	data: any[],
	xKey: string,
	forecastStartMonth: string | undefined,
	showSeriesTotal: boolean,
	axisFormatter: (value: number) => string,
	showEndTotal: boolean,
) => {
	const forecastStartIndex =
		getForecastStartIndex(
			data,
			xKey,
			forecastStartMonth,
		);

	if (forecastStartIndex === -1) {
		return series.map(
			(item: any, index: number) => {
				const color =
					item.color ??
					getDynamicLineColor(
						index,
						series.length,
					);

				const total = getSeriesTotal(
					data,
					item.key,
				);

				return createLineSeriesOption(
					item,
					data.map(
						(dataItem: any) =>
							dataItem?.[item.key],
					),
					'solid',
					color,
					showSeriesTotal,
					total,
					axisFormatter,
					showEndTotal,
				);
			},
		);
	}

	return series.flatMap(
		(item: any, index: number) => {
			const color =
				item.color ??
				getDynamicLineColor(
					index,
					series.length,
				);

			const total = getSeriesTotal(
				data,
				item.key,
			);

			return [
				createLineSeriesOption(
					item,
					createActualSeriesData(
						data,
						item.key,
						forecastStartIndex,
					),
					'solid',
					color,
					showSeriesTotal,
					total,
					axisFormatter,
					// Never show the end-of-line total on the actual (solid)
					// segment when a forecast segment follows it — its last
					// point is visually mid-line, not the line's true end,
					// and showing it here would duplicate the dotted
					// segment's label with the same total value.
					false,
				),
				createLineSeriesOption(
					item,
					createForecastSeriesData(
						data,
						item.key,
						forecastStartIndex,
					),
					'dotted',
					color,
					showSeriesTotal,
					total,
					axisFormatter,
					showEndTotal,
				),
			];
		},
	);
};

export const buildMultiLineChartOptions = ({
	data,
	colors,
	showGridLines = false,
	isCost = false,
	xKey = 'name',
	xAxisLabel = 'Month',
	xLabelRotate = 0,
	xLabelTruncate = 8,
	series = [],
	tooltipFormatter,
	lastUpdated,
	centerYAtZero = false,
	legendPosition = 'top',
	forecastStartMonth,
	showSeriesTotal = false,
	showEndTotal = false,
	showValuesInMillions = false,
	yMin,
	yMax,
	yInterval,
	yAxisFormatter
}: any) => {
	if (!data?.length || !series?.length) return null;

	const labels = data.map(
		(item: any) => item?.[xKey],
	);

	const allValues: number[] = [];

	series.forEach((item: any) => {
		data.forEach((dataItem: any) => {
			const value = dataItem?.[item.key];

			if (typeof value === 'number') {
				allValues.push(value);
			}
		});
	});

	const minData = allValues.length
		? Math.min(...allValues)
		: 0;

	const maxData = allValues.length
		? Math.max(...allValues)
		: 0;

	const finalInterval =
		yInterval !== undefined
			? yInterval
			: getIntervalByRule(maxData);

	const maxDataRounded = roundUpToInterval(
		maxData,
		finalInterval,
	);

	const absMax = centerYAtZero
		? roundUpToInterval(
			Math.max(
				Math.abs(minData),
				Math.abs(maxData),
			),
			finalInterval,
		) || finalInterval
		: maxDataRounded;

	let finalYMin = 0;

	if (yMin !== undefined) {
		finalYMin = yMin;
	} else if (centerYAtZero) {
		finalYMin = -absMax;
	} else if (minData < 0) {
		finalYMin = minData;
	}

	let finalYMax = maxDataRounded;

	if (yMax !== undefined) {
		finalYMax = yMax;
	} else if (centerYAtZero) {
		finalYMax = absMax;
	}

	const defaultFormatter = showValuesInMillions
		? getMillionsFormatter()
		: getAxisFormatter(isCost);

	const axisFormatter = yAxisFormatter ?? defaultFormatter;

	const seriesOptions = getSeriesOptions(
		series,
		data,
		xKey,
		forecastStartMonth,
		showSeriesTotal,
		axisFormatter,
		showEndTotal,
	);

	const legendOptions =
		legendPosition === 'bottom'
			? {
				type: 'scroll',
				orient: 'horizontal',
				bottom: 10,
				left: 20,
				right: 20,
				icon: 'roundRect',
				itemWidth: 16,
				itemHeight: 16,
				itemGap: 16,
				pageIconColor:
					colors.widget.chart
						.legendActiveUpDownIconColor,
				pageIconInactiveColor:
					colors.widget.chart
						.legendInactiveUpDownIconColor,
				pageTextStyle: {
					color:
						colors.widget.chart
							.legendTextColor,
				},
				textStyle: {
					color:
						colors.widget.chart
							.legendTextColor,
				},
			}
			: {
				type: 'scroll',
				orient: 'horizontal',
				top: 0,
				left: 20,
				right: 20,
				icon: 'roundRect',
				itemWidth: 16,
				itemHeight: 16,
				itemGap: 16,
				pageIconColor:
					colors.widget.chart
						.legendActiveUpDownIconColor,
				pageIconInactiveColor:
					colors.widget.chart
						.legendInactiveUpDownIconColor,
				pageTextStyle: {
					color:
						colors.widget.chart
							.legendTextColor,
				},
				textStyle: {
					color:
						colors.widget.chart
							.legendTextColor,
				},
			};

	return {
		backgroundColor: 'transparent',

		legend: legendOptions,

		graphic: getGraphicOptions(
			lastUpdated,
			colors,
		),

		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'line',
			},
			formatter: getTooltipFormatter(
				tooltipFormatter,
				axisFormatter,
			),
		},

		grid: {
			left: '8%',
			// showEndTotal's label sits further right (via its own `distance`)
			// than showSeriesTotal's per-point labels, and needs more room
			// when both render together at the line's last point — reserve
			// extra margin rather than letting it get clipped by the plot area.
			right: showEndTotal
				? '20%'
				: showSeriesTotal
					? '15%'
					: '4%',
			top:
				legendPosition === 'bottom'
					? '5%'
					: 55,
			bottom:
				legendPosition === 'bottom'
					? 80
					: '10%',
			containLabel: true,
		},

		xAxis: {
			type: 'category',
			data: labels,
			name: xAxisLabel,
			nameLocation: 'middle',
			nameGap: 35,
			axisTick: {
				show: false,
			},
			axisLabel: {
				rotate: xLabelRotate,
				color:
					colors.widget.chart
						.labelColorSecondary,
				formatter: (value: any) =>
					String(value).length >
						xLabelTruncate
						? `${String(value).slice(
							0,
							xLabelTruncate,
						)}…`
						: value,
			},
			nameTextStyle: {
				color:
					colors.widget.chart
						.labelColorSecondary,
				fontWeight: 500,
			},
		},

		yAxis: {
			type: 'value',
			min: finalYMin,
			max: finalYMax,
			interval: finalInterval,
			splitLine: {
				show: showGridLines,
			},
			axisLabel: {
				formatter: axisFormatter,
				color:
					colors.widget.chart
						.labelColorSecondary,
				showMinLabel: true,
				showMaxLabel: true,
			},
			axisLine: {
				show: true,
				lineStyle: {
					type: 'dotted',
					width: 1,
				},
			},
		},

		series: seriesOptions,
	};
};

// SLA Barchart Options
export const buildBarChartOptions = ({
	data,
	orientation,
	showGridLines,
	colors,
	colorScheme,
	barColor,
	slaTarget,
	slaLabel,
	tooltipFormatter
}: any) => {
	if (!data?.length) return null;

	const labels = data.map((d: any) => d.name);
	const values = data.map((d: any) => d.value);
	const isVertical = orientation === "vertical";

	// Min Y Axis Logic
	const lowestValue =
		data.length > 0 ? Math.min(...data.map((d: any) => d.value)) : 100;

	const minYValue = lowestValue > 99.9 ? 99.9 : 99.5;

	return {
		backgroundColor: "transparent",

		tooltip: {
			trigger: "axis",
			formatter:
				tooltipFormatter ||
				((params: any) =>
					`${params[0].name}<br/>Value: ${params[0].value}`)
		},

		grid: {
			left: "5%",
			right: isVertical ? "5%" : "10%",
			top: "5%",
			bottom: "5%",
			containLabel: true
		},

		xAxis: isVertical
			? {
				type: "category",
				data: labels,
				axisTick: { show: false },
				axisLabel: {
					rotate: 90,
					color: colors.widget.chart.labelColorSecondary,
					formatter: (value: string) =>
						value.length > 8 ? value.substring(0, 8) + "…" : value
				}
			}
			: {
				type: "value",
				min: minYValue,
				max: 100,
				splitLine: { show: showGridLines },
				axisLabel: {
					color: colors.widget.chart.labelColorSecondary,
					formatter: (val: number) => val.toFixed(2)
				}
			},

		yAxis: isVertical
			? {
				type: "value",
				min: minYValue,
				max: 100,
				splitLine: { show: showGridLines },
				axisLabel: {
					color: colors.widget.chart.labelColorSecondary,
					formatter: (val: number) => val.toFixed(2)
				}
			}
			: {
				type: "category",
				data: labels,
				axisLabel: {
					color: colors.widget.chart.labelColorSecondary,
					formatter: (value: string) =>
						value.length > 8 ? value.substring(0, 8) + "…" : value
				}
			},

		series: [
			{
				type: "bar",
				data: values,
				label: {
					show: true,
					position: isVertical ? "top" : "right",
					color: colors.widget.text.main
				},

				// Color coding logic
				itemStyle: {
					color: (params: any) => {
						if (!colorScheme) {
							return barColor || colors.widget.colors.color2;
						}

						const target = slaTarget ?? 0;

						if (params.value >= target) {
							return colorScheme.above;
						}
						return colorScheme.below;
					}
				},

				...(slaTarget
					? {
						markLine: {
							symbol: "none",
							label: {
								show: true,
								position: "end",
								align: "right",
								verticalAlign: "top",
								distance: 8,
								formatter: () => `${slaTarget} ${slaLabel}`,
								color: colors.widget.colors.color3
							},
							lineStyle: {
								color: colors.widget.colors.color3,
								width: 3,
								type: "solid"
							},
							data: [
								isVertical
									? { yAxis: slaTarget, name: slaLabel }
									: { xAxis: slaTarget, name: slaLabel }
							]
						}
					}
					: {})
			}
		]
	};
};

// Race chart
export const buildRaceBarChartOptions = ({
	data,
	colors,
	showGridLines = false,
	tooltipFormatter,
	stackSeries, // [{ key, name, color }]
	seriesColors,
	valueType = "number", // cost | storage | number
	storageUnit = "GB",   // KB | MB | GB | TB | PB
}: any) => {
	if (!data?.length) return null;

	/* ================= STORAGE HELPERS ================= */
	const STORAGE_UNIT_INDEX: Record<string, number> = {
		KB: 0,
		MB: 1,
		GB: 2,
		TB: 3,
		PB: 4,
	};

	const RAW_UNIT_FACTOR = Math.pow(1024, STORAGE_UNIT_INDEX[storageUnit]);

	const toDisplayUnit = (raw: number) => raw / RAW_UNIT_FACTOR;

	const getStorageInterval = (max: number) => {
		const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
		const normalized = max / magnitude;

		if (normalized <= 2) return magnitude / 2;
		if (normalized <= 5) return magnitude;
		return magnitude * 2;
	};

	const formatValue = (rawValue: number) => {
		if (valueType === "cost") {
			return `$${rawValue.toLocaleString()}`;
		}

		if (valueType === "storage") {
			return `${toDisplayUnit(rawValue).toFixed(2)} ${storageUnit}`;
		}

		if (rawValue >= 1_000_000_000)
			return `${(rawValue / 1_000_000_000).toFixed(2)}B`;
		if (rawValue >= 1_000_000)
			return `${(rawValue / 1_000_000).toFixed(2)}M`;
		if (rawValue >= 1_000)
			return `${(rawValue / 1_000).toFixed(2)}K`;

		return rawValue.toString();
	};

	const sortedData = [...data].sort((a: any, b: any) => {
		if (Array.isArray(stackSeries) && stackSeries.length > 1) {
			const totalA = stackSeries.reduce(
				(sum: number, s: any) => sum + (a[s.key] ?? 0),
				0
			);
			const totalB = stackSeries.reduce(
				(sum: number, s: any) => sum + (b[s.key] ?? 0),
				0
			);
			return totalB - totalA;
		}

		return (b.value ?? 0) - (a.value ?? 0);
	});

	const labels = sortedData.map((d: any) => d.name);
	const isStacked = Array.isArray(stackSeries) && stackSeries.length > 1;

	/* ================= STORAGE AXIS SCALE ================= */
	let axisMax: number | undefined;
	let axisInterval: number | undefined;

	if (valueType === "storage") {
		const maxRawValue = isStacked
			? Math.max(
				...sortedData.map((d: any) =>
					stackSeries.reduce(
						(sum: number, s: any) => sum + (d[s.key] ?? 0),
						0
					)
				)
			)
			: Math.max(...sortedData.map((d: any) => d.value ?? 0));

		const maxInDisplayUnit = toDisplayUnit(maxRawValue);
		const intervalDisplay = getStorageInterval(maxInDisplayUnit);

		const intervalRaw = intervalDisplay * RAW_UNIT_FACTOR;

		axisMax =
			Math.ceil(maxRawValue / intervalRaw) * intervalRaw;

		axisInterval = intervalRaw;

	}

	const series = isStacked
		? stackSeries.map((s: any, idx: number) => ({
			name: s.name,
			type: "bar",
			stack: "total",
			data: sortedData.map((d: any) => d[s.key] ?? 0),
			barCategoryGap: "30%",
			barGap: "0%",
			label: { show: false },
			itemStyle: {
				color:
					s.color ||
					seriesColors?.[s.key] ||
					colors.widget.colors[`color${idx + 1}`],
			},
		}))
		: [
			{
				name: stackSeries?.[0]?.name || "Value",
				type: "bar",
				data: sortedData.map((d: any) => d.value),
				barCategoryGap: "30%",
				label: { show: false },
				itemStyle: {
					color:
						stackSeries?.[0]?.color ||
						seriesColors?.value ||
						colors.widget.colors.color2,
				},
			},
		];

	return {
		backgroundColor: "transparent",

		legend: {
			show: isStacked,
			top: 0,
			left: 0,
			textStyle: {
				color: colors.widget.text.main,
				fontSize: 12,
				fontWeight: 500,
			},
		},

		tooltip: {
			trigger: "axis",
			axisPointer: { type: "shadow" },
			formatter:
				tooltipFormatter ||
				((params: any[]) => {
					if (!params?.length) return "";

					if (params.length === 1) {
						const p = params[0];
						return `${p.name} : <b>${formatValue(p.value)}</b>`;
					}

					const title = params[0].name;
					const rows = params
						.map(
							(p) =>
								`${p.marker} ${p.seriesName} : <b>${formatValue(
									p.value
								)}</b>`
						)
						.join("<br/>");

					return `<b>${title}</b><br/>${rows}`;
				}),
		},

		grid: {
			left: "5%",
			right: "8%",
			top: isStacked ? "15%" : "10%",
			bottom: "5%",
			containLabel: true,
		},

		xAxis: {
			type: "value",
			min: 0,
			max: axisMax,
			interval: axisInterval,
			minInterval: 1,
			scale: false,
			splitLine: { show: showGridLines },
			axisLabel: {
				color: colors.widget.chart.labelColorSecondary,
				formatter: (val: number) =>
					valueType === "storage"
						? `${Math.round(toDisplayUnit(val))} ${storageUnit}`
						: formatValue(val),
			},
		},

		yAxis: {
			type: "category",
			data: labels,
			inverse: true,
			axisLabel: {
				color: colors.widget.chart.labelColorSecondary,
				overflow: "truncate",
				ellipsis: "...",
				formatter: (value: string) =>
					value.length > 15 ? `${value.slice(0, 15)}...` : value,
			},
		},

		series,
	};
};

// Metric Area/Line Chart Options
type BuildOptionsParams = {
	chartData: {
		xAxis: string[];
		series: {
			name: string;
			color: string;
			data: number[];
		}[];
	};
	seriesConfig: {
		variant?: 'area' | 'line';
	}[];
	colors: any;
	isCost: boolean;
	xAxisLabel?: string;
	formatValue: (value: number, isCost: boolean) => string;
};

export const buildMetricAreaLineOptions = ({
	chartData,
	seriesConfig,
	colors,
	isCost,
	xAxisLabel,
	formatValue,
}: BuildOptionsParams) => {
	return {
		backgroundColor: colors?.widget?.background?.chart,
		textStyle: { color: colors?.widget?.chart?.labelColorSecondary },

		tooltip: {
			trigger: 'axis',
			formatter: (params: any[]) => {
				const date = params[0]?.axisValueLabel;
				let tooltipContent = `<div><strong>${date}</strong></div>`;

				params.forEach((item) => {
					const formattedValue = formatValue(item.value, isCost);

					tooltipContent += `
            <div>
              ${item.marker}
              <strong>${item.seriesName}</strong>: 
              <strong>${formattedValue}</strong>
            </div>
          `;
				});

				return tooltipContent;
			},
		},

		legend: {
			left: 0,
			bottom: 0,
			icon: 'rect',
			textStyle: { color: colors?.widget?.chart?.legendTextColor },
		},

		grid: {
			left: '5%',
			right: '5%',
			bottom: '10%',
			containLabel: true,
		},

		xAxis: {
			type: 'category',
			data: chartData.xAxis,
			name: xAxisLabel,
			nameLocation: 'middle',
			nameGap: 30,
			nameTextStyle: { color: colors?.widget?.chart?.labelColorSecondary },
			axisLabel: { color: colors?.widget?.chart?.labelColorSecondary },
			boundaryGap: false,
		},

		yAxis: {
			type: 'value',
			axisLabel: {
				color: colors?.widget?.chart?.labelColorSecondary,
				formatter: (value: number) => formatValue(value, isCost),
			},
			splitLine: false,
		},

		series: chartData.series.map((s, index) => {
			const config = seriesConfig[index];
			const isLine = config?.variant === 'line';

			return {
				name: s.name,
				type: 'line',
				data: s.data,
				showSymbol: false,

				...(isLine
					? {
						z: 10,
						itemStyle: { color: s.color },
						lineStyle: {
							width: 3,
							color: s.color,
						},
					}
					: {
						stack: 'total',
						itemStyle: { color: s.color },
						lineStyle: { width: 2, color: s.color },
						areaStyle: {
							opacity: 1,
							color: s.color,
						},
					}),
			};
		}),
	};
};

// grouped barchart
export type ChartValue = number | null;
export interface SegmentedBarChartSeries {
	name: string;
	data: ChartValue[];
	type?: "bar" | "line";
	color?: string;

	/**
	 * number = left Y axis
	 * percentage = right Y axis
	 */
	yAxisType?: "number" | "percentage";

	/**
	 * Useful for average lines.
	 * Example: symbol: "none"
	 */
	symbol?: string;

	/**
	 * true for smooth SLA line.
	 * false for straight average line.
	 */
	smooth?: boolean;

	labelPosition?: "top" | "bottom" | "left" | "right" | "inside";

	lineStyle?: {
		type?: "solid" | "dashed" | "dotted";
		width?: number;
	};

	barGap?: string;
}

export interface SegmentedBarChartOptions {
	orientation: "vertical" | "horizontal";
	mode?: "stacked" | "grouped";
	colors?: any;
	colorScheme?: string[];
	showGridLines?: boolean;
	xAxisLabel?: string;
	showBarLabel?: boolean;
	showLineLabel?: boolean;
	isCost?: boolean;
	showLegendOnTop?: boolean;
}

export const buildSegmentedBarChartOptions = (
	categories: string[],
	series: SegmentedBarChartSeries[],
	{
		orientation,
		mode = "grouped",
		colors,
		colorScheme,
		showGridLines = false,
		xAxisLabel,
		showBarLabel = true,
		showLineLabel = true,
		isCost = false,
		showLegendOnTop = false
	}: SegmentedBarChartOptions
) => {
	const defaultColors = [
		colors.widget.colors.color1,
		colors.widget.colors.color2,
		colors.widget.colors.color3,
		colors.widget.colors.color4,
		colors.widget.colors.color5
	];

	const palette = colorScheme?.length ? colorScheme : defaultColors;

	const hasPercentageSeries = series.some(
		(s) => s.yAxisType === "percentage"
	);

	const formatCost = (v: number) =>
		Number.isFinite(v) ? `$${Math.round(v).toLocaleString()}` : "";

	const formatNumber = (v: number) =>
		Number.isFinite(v) ? `${Math.round(v).toLocaleString()}` : "";

	const formatPercentage = (v: number) =>
		Number.isFinite(v) ? `${v}%` : "";

	const valueFormatter = (
		value: number,
		valueType: "number" | "percentage" = "number"
	) => {
		if (!Number.isFinite(value)) return "";

		if (valueType === "percentage") {
			return formatPercentage(value);
		}

		return isCost ? formatCost(value) : formatNumber(value);
	};

	const getNumericValues = (
		valueType: "number" | "percentage"
	): number[] => {
		return series
			.filter((s) => (s.yAxisType || "number") === valueType)
			.flatMap((s) => s.data)
			.filter((value): value is number => typeof value === "number");
	};

	const leftAxisValues = getNumericValues("number");

	const maxLeftValue = Math.max(0, ...leftAxisValues);
	const leftStepSize = Math.max(
		100,
		Math.ceil(maxLeftValue / 4 / 100) * 100
	);
	const leftAxisMax =
		maxLeftValue > 0
			? Math.ceil(maxLeftValue / leftStepSize) * leftStepSize
			: 100;

	const processedSeries = series.map((s, index) => {
		const chartType = s.type || "bar";
		const yAxisType = s.yAxisType || "number";
		const color = s.color || palette[index % palette.length];
		const isLine = chartType === "line";

		return {
			name: s.name,
			type: chartType,
			data: s.data,
			yAxisIndex: hasPercentageSeries && yAxisType === "percentage" ? 1 : 0,
			stack:
				chartType === "bar" && mode === "stacked"
					? "stacked-bars"
					: undefined,
			smooth: isLine ? Boolean(s.smooth) : undefined,
			symbol: isLine ? s.symbol || "circle" : undefined,
			barGap: chartType === "bar" ? s.barGap || "10%" : undefined,
			label: {
				show: isLine ? showLineLabel : showBarLabel && mode !== "stacked",
				position:
					s.labelPosition ||
					(isLine
						? "top"
						: orientation === "vertical"
							? "top"
							: "right"),
				color: colors?.widget?.chart?.labelColorSecondary || "#666",
				fontSize: 10,
				formatter: (params: any) =>
					valueFormatter(params.value, yAxisType)
			},
			itemStyle: {
				color
			},
			lineStyle: isLine
				? {
					color,
					type: s.lineStyle?.type || "solid",
					width: s.lineStyle?.width || 2
				}
				: undefined
		};
	});

	return {
		backgroundColor: colors?.widget?.background?.chart,
		textStyle: {
			color: colors?.widget?.chart?.labelColorSecondary
		},

		tooltip: {
			trigger: "axis",
			formatter: (params: any[]) => {
				const title = params?.[0]?.axisValueLabel ?? "";

				const rows = params
					.map((p) => {
						const matchedSeries = series.find(
							(item) => item.name === p.seriesName
						);

						const valueType = matchedSeries?.yAxisType || "number";

						return `${p.marker} ${p.seriesName}: <strong>${valueFormatter(
							p.value,
							valueType
						)}</strong>`;
					})
					.join("<br/>");

				return `${title}<br/>${rows}`;
			}
		},

		legend: {
			type: "scroll",
			left: 0,
			...(showLegendOnTop ? { top: 0 } : { bottom: 0 }),
			textStyle: {
				color: colors?.widget?.chart?.legendTextColor
			},
			pageIconColor: colors?.widget?.chart?.legendTextColor,
			pageIconInactiveColor: "#444C63",
			pageTextStyle: {
				color: colors?.widget?.chart?.legendTextColor,
				fontSize: 10
			},
			pageButtonItemGap: 5,
			pageButtonGap: 10,
			data: processedSeries.map((s) => s.name)
		},

		grid: {
			left: "5%",
			right: hasPercentageSeries ? "7%" : "5%",
			bottom: showLegendOnTop ? "12%" : "18%",
			top: showLegendOnTop ? "18%" : "5%",
			containLabel: true
		},

		xAxis:
			orientation === "vertical"
				? [
					{
						name: xAxisLabel || undefined,
						nameGap: xAxisLabel ? 35 : 20,
						nameLocation: "middle",
						type: "category",
						data: categories,
						axisLabel: {
							color: colors?.widget?.chart?.labelColorSecondary
						},
						axisTick: {
							show: false
						},
						axisLine: {
							show: true
						}
					}
				]
				: [
					{
						type: "value",
						axisLabel: {
							color: colors?.widget?.chart?.labelColorSecondary,
							formatter: (val: number) => valueFormatter(val)
						},
						splitLine: {
							show: showGridLines
						}
					}
				],

		yAxis:
			orientation === "vertical"
				? [
					{
						type: "value",
						min: 0,
						max: leftAxisMax,
						interval: leftStepSize,
						axisLabel: {
							color: colors?.widget?.chart?.labelColorSecondary,
							formatter: (val: number) => valueFormatter(val)
						},
						splitLine: {
							show: showGridLines
						}
					},
					...(hasPercentageSeries
						? [
							{
								type: "value",
								min: 0,
								max: 100,
								interval: 25,
								axisLabel: {
									color: colors?.widget?.chart?.labelColorSecondary,
									formatter: "{value}%"
								},
								splitLine: {
									show: false
								}
							}
						]
						: [])
				]
				: [
					{
						type: "category",
						data: categories,
						axisLabel: {
							color: colors?.widget?.chart?.labelColorSecondary
						}
					}
				],

		series: processedSeries
	};
};

type BasicPieChartOptionsParams = {
	theme: Theme;
	seriesData: any;
	unit: string;
	containerWidth: any;
	colorMapping: any;
	showDollar: boolean;
	showPercentage: boolean;
	customChartPosition?: any;
};

export const getBasicPieChartOptions = ({
	theme,
	seriesData,
	unit,
	containerWidth,
	colorMapping,
	showDollar,
	showPercentage,
	customChartPosition,
}: BasicPieChartOptionsParams) => {
	const colors = theme.palette;
	const option = {
		tooltip: {
			backgroundColor: colors.widget.chart.tooltipBg,
			textStyle: {
				color: colors.widget.chart.tooltipText,
			},
			formatter: function (params: any) {
				let value = params.value;
				const displayValue = showDollar ? `$${value}` : `${value} ${unit}`;
				return `${params.name} : ${params.percent}%  (${displayValue})`;
			},
			position: 'inside'
		},
		legend: {
			// top: '70%',
			type: 'scroll',
			// Scroll arrow icons
			pageIconColor: colors.widget.chart.legendActiveUpDownIconColor,
			pageIconInactiveColor: colors.widget.chart.legendInactiveUpDownIconColor,
			pageTextStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: 10
			},
			pageButtonItemGap: 5,
			pageButtonGap: 10,
			orient: 'vertical',
			right: '4%',
			top: 'middle',
			icon: 'roundRect',
			itemWidth: 14,
			itemHeight: 14,
			data: seriesData.map((item: any) => item && item.name ? item.name : ''),
			textStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: containerWidth < 370 ? 10 : 12,
				fontWeight: 500,
				rich: {
					bold: {
						fontWeight: "bold",
						color: colors.widget.chart.tooltipText,
					}
				}
			},
			formatter: function (name: string) {
				const d = seriesData.find((i: any) => i.name === name);
				if (!d) return name;

				const value = d.value;
				const displayValue = showDollar ? `$${value}` : `${value} ${unit}`;
				return `${name} - {bold|${displayValue}}`;
			},
		},
		series: [
			{
				type: 'pie',
				radius: '60%',
				center: customChartPosition ? customChartPosition : ['22%', '50%'],
				selectedMode: 'single',
				label: {
					show: false,
					position: 'inside',
					fontSize: 15,
					textStyle: {
						align: 'center',
						verticalAlign: 'middle'
					},
					formatter: function (params: any) {
						if (showPercentage) {
							return `${params.percent.toFixed(0)}%`;
						}
						return '';
					},
					rich: {
						a: {
							color: colors.widget.chart.labelColor,
							fontSize: 12,
							lineHeight: 20
						},
						b: {
							color: colors.widget.chart.labelColor,
							fontSize: 12
						}
					},
					color: colors.widget.chart.labelColor,
				},
				data: seriesData.map((item: any) => ({
					name: item.name,
					value: item.value,
					itemStyle: colorMapping?.[item.name] ? { color: colorMapping[item.name] } : undefined,
					label: showPercentage
						? {
							formatter: function (params: any) {
								if (params.percent < 8) return '';
								return `${params.percent.toFixed(0)}%`;
							}
						}
						: {
							formatter: function (params: any) {
								if (params.percent < 8) return '';
								return `${item.formattedName}`;
							}
						}
				})),
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: 'rgba(0, 0, 0, 0.5)'
					}
				}
			}
		],
		textStyle: {
			fontFamily: 'Montserrat'
		}
	};

	return option;
};

export const getBasicDonutChartOptions = (
	theme: Theme,
	title: string,
	seriesData: any,
	containerWidth: any,
	colorMapping: any,
	showDollar: boolean,
	customChartPosition?: any
) => {
	const colors = theme.palette;
	const isMobile = containerWidth < 480;

	return {
		graphic: [
			{
				type: "text",
				left: "60%",
				top: "15%",
				style: {
					text: title,
					fill: colors.widget.text.title,
					font: "500 18px Montserrat",
				}
			}
		],
		tooltip: {
			backgroundColor: colors.widget.chart.tooltipBg,
			textStyle: { color: colors.widget.chart.tooltipText },
			formatter: (params: any) => {
				const value = params.value;
				const displayValue = showDollar ? `$${value}` : `${value}`;
				return `${params.name} : ${params.percent}%  (${displayValue})`;
			},
			position: "right"
		},

		legend: {
			type: "scroll",
			orient: "vertical",
			left: "60%",
			top: "middle",
			icon: "roundRect",
			itemWidth: 12,
			itemHeight: 12,
			itemGap: 10,
			textStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: containerWidth < 370 ? 11 : 13,
				fontWeight: 500,
				rich: {
					bold: {
						fontWeight: "bold",
						color: colors.widget.chart.tooltipText,
					}
				}
			},
			data: seriesData.map((i: any) => i.name),
			formatter: function (name: string) {
				const d = seriesData.find((it: any) => it.name === name);
				if (!d) return name;

				const displayValue = showDollar ? `$${d.value}` : `${d.value}`;
				return `${name} - {bold|${displayValue}}`;
			}
		},

		series: [
			{
				type: "pie",
				radius: isMobile ? ["30%", "55%"] : ["55%", "80%"],
				center: customChartPosition ? customChartPosition : ["28%", "50%"],
				avoidLabelOverlap: false,
				selectedMode: false,

				label: { show: false },

				data: seriesData.map((item: any) => ({
					name: item.name,
					value: item.value,
					itemStyle: {
						color: colorMapping?.[item.name] || undefined,
					}
				})),

				emphasis: {
					scale: true,
					itemStyle: {
						shadowBlur: 8,
						shadowOffsetX: 0,
						shadowColor: "rgba(0,0,0,0.3)"
					}
				}
			}
		],

		textStyle: { fontFamily: "Montserrat" }
	};
}


export const getProgressChartOptions = (
	data: any,
	envNames: any,
	seriesData: any,
	containerWidth: any,
	chartByCost: any,
	axisMax?: number
) => {

	const getAxisName = (item: any) =>
		item.axisName || item.programname || item.name || '';

	const truncateText = (text: string, max: number, suffix = '...') =>
		text.length > max ? text.substring(0, max) + suffix : text;

	const shouldUseShortLabel = () =>
		(chartByCost === 1 && data[0]?.unit !== "count") || chartByCost === 0;

	const buildAxisData = () => {
		return [...data]
			.map((item: any) => {
				const axisName = getAxisName(item);

				if (shouldUseShortLabel()) {
					return truncateText(axisName, 10);
				}

				const suffix = containerWidth < 370 ? '.' : '...';
				return truncateText(axisName, 7, suffix);
			})
			.reverse();
	};

	const getLegendFontSize = () => {
		if (chartByCost !== 0) return 12;

		if (window.innerWidth < 280) return 3;
		if (window.innerWidth < 346) return 4;
		if (window.innerWidth < 396) return 6;
		if (window.innerWidth < 423) return 10;

		return 12;
	};

	const getLegendItemGap = () => {
		if (chartByCost !== 0) return 10;

		if (window.innerWidth < 280) return 0;
		if (window.innerWidth < 335) return 1;
		if (window.innerWidth < 396) return 3;

		return 10;
	};

	const buildTooltipFormatter = () => {
		return (params: any) => {
			const index = data.length - 1 - params[0].dataIndex;
			const item = data[index];

			const fullName =
				item.axisName || item.programname || item.name;

			const sector = item.sector;

			let tooltipText = `${fullName}<br/>`;

			if (sector) {
				tooltipText += `Sector: ${sector}<br/>`;
			}

			params.forEach((param: any) => {
				const isNonCostSeries = [
					'VIEWS',
					'WORKBOOKS',
					'DATASOURCES',
					'DATAFLOWS',
					'ACTIVITYCOUNT'
				].includes(param.seriesName);

				if (isNonCostSeries) {
					const value = formatNumberByUnits(param.value);
					tooltipText += `${param.marker} ${param.seriesName}: ${value}<br/>`;
					return;
				}

				if (param.seriesName !== 'TOTAL_COST') {
					const value = formatNumberByUnits(param.value);
					tooltipText += `${param.marker} ${param.seriesName}: $${value}<br/>`;
				}
			});

			if (chartByCost !== 1) {
				const totalCost = item.total_cost;
				const formattedTotalCost = formatNumberByUnits(totalCost);
				tooltipText += `Total Cost: $${formattedTotalCost}`;
			}

			return tooltipText;
		};
	};

	const option = {
		tooltip: {
			trigger: 'axis',
			axisPointer: { type: 'shadow' },
			backgroundColor: '#003771',
			textStyle: { color: '#A9ADCB' },
			confine: true,
			formatter: buildTooltipFormatter()
		},

		legend: {
			show: chartByCost !== 1,
			data: envNames.map((env: string) => env.toUpperCase()),
			textStyle: {
				color: '#A7ADCB',
				fontSize: getLegendFontSize()
			},
			itemGap: getLegendItemGap(),
			bottom: chartByCost === 0 ? '5%' : '0%',
			left: '1%'
		},

		grid: {
			left: '1%',
			right: '10%',
			bottom: '10%',
			containLabel: true
		},

		xAxis: {
			type: 'value',
			...(axisMax ? { max: axisMax } : {}),
			...(data[0]?.unit === "count" ? { minInterval: 1 } : {}),
			show: chartByCost === 1,
			axisLabel: {
				color: '#A7ADCB',
				formatter: (value: number) => formatNumberByUnits(value)
			},
			splitLine: {
				show: false,
				lineStyle: {
					type: 'dashed',
					color: '#004477'
				}
			}
		},

		yAxis: {
			type: 'category',
			data: buildAxisData(),
			axisLine: { show: !(chartByCost === 0 || chartByCost === 1) },
			axisTick: { show: !(chartByCost === 0 || chartByCost === 1) },
			axisLabel: { color: '#A7ADCB' }
		},

		series: seriesData,

		textStyle: {
			fontFamily: 'Montserrat'
		}
	};

	return option;
};

export const getLineChartOptions = (
	chartData: any,
	colorMapping: any,
	gradientMapping: any,
	hasLegend: any,
	colors: any
) => {
	if (!chartData || chartData.length === 0) {
		return { chartOption: null };
	}

	const xAxisData = chartData.map((item: any) => item.Year);
	const seen = new Set<string>();
	const seriesKeys: string[] = [];

	chartData.forEach((item: any) => {
		Object.keys(item).forEach(key => {
			if (key !== 'Year' && !seen.has(key)) {
				seen.add(key);
				seriesKeys.push(key);
			}
		});
	});

	const congestedYears = new Set<string>();
	const topLabelsPerYear: Record<string, Set<string>> = {};

	chartData.forEach((item: any) => {
		const year = String(item.Year);

		const valueObjects = seriesKeys
			.map(key => ({ key, value: item[key] }))
			.filter(d => typeof d.value === 'number' && !isNaN(d.value));

		let overlapCount = 0;
		for (let i = 0; i < valueObjects.length; i++) {
			for (let j = i + 1; j < valueObjects.length; j++) {
				if (Math.abs(valueObjects[i].value - valueObjects[j].value) < 1_000_000) {
					overlapCount++;
				}
			}
		}

		if (overlapCount >= 2) {
			congestedYears.add(year);

			const sortedValueObjects = [...valueObjects].sort(
				(a, b) => b.value - a.value
			);
			const topEntries = sortedValueObjects.slice(0, 1);

			topLabelsPerYear[year] = new Set(topEntries.map(d => d.key));
		}
	});

	const seriesData = seriesKeys.map(key => {
		return {
			name: key,
			type: 'line',
			smooth: false,
			data: chartData.map((item: any) => item.hasOwnProperty(key) ? item[key] : 0),
			itemStyle: { color: colorMapping[key] || '#009BD8' },
			...(gradientMapping[key] && Object.keys(gradientMapping[key]).length > 0 && {
				areaStyle: {
					color: {
						type: 'linear',
						x: 0, y: 1, x2: 0, y2: 0,
						colorStops: [
							{ offset: 0, color: gradientMapping[key].bottom },
							{ offset: 1, color: gradientMapping[key].top }
						]
					}
				}
			}),
			label: {
				show: true,
				position: 'top',
				color: colors.widget.chart.legendTextColor,
				fontSize: window.innerWidth < 600 ? 8 : 12,
				formatter: (params: any) => {
					const year = String(params.name);
					const currentKey = key;

					const isTop = topLabelsPerYear[year]?.has(currentKey);
					const isCongested = congestedYears.has(year);

					if (!params.value || params.value === 0) return '';

					if (isCongested) {
						return isTop ? `$${formatNumberUnits(params.value)}` : '';
					}
					return `$${formatNumberUnits(params.value)}`;
				}
			}
		};
	});

	const chartOption = {
		title: {
			show: chartData.length === 0,
			textStyle: {
				color: '#bcbcbc',
				fontWeight: 'normal',
				fontSize: 18
			},
			text: 'No data for the selected filters',
			left: 'center',
			top: 'center'
		},
		grid: {
			top: '13%',
			right: '10%',
			bottom: hasLegend ? '20%' : '5%',
			backgroundColor: 'transparent',
			containLabel: true
		},
		xAxis: {
			type: 'category',
			data: xAxisData,
			axisLine: {
				show: false,
			},
			axisTick: { show: false },
			splitLine: {
				show: false,
				lineStyle: {
					type: 'dashed',
					color: '#004477'
				}
			},
			boundaryGap: false,
			axisLabel: {
				color: colors.widget.chart.legendTextColor,
				formatter: function (value: any) {
					return !hasLegend ? `${moment(value).format('MMM-YY')}` : `${moment(value, 'YYYY').format('YYYY')}`;
				}
			}
		},
		yAxis: {
			type: 'value',
			splitLine: {
				show: false,
				lineStyle: {
					type: 'dashed',
					color: '#004477'
				}
			},
			axisLabel: {
				show: false,
			}
		},
		series: seriesData,
		tooltip: {
			trigger: 'axis',
			backgroundColor: colors.widget.chart.tooltipBg,
			// confine: true,
			formatter: function (params: any) {
				if (hasLegend) {
					let tooltipText = '';
					const year = params[0]?.name;
					params.forEach((item: any) => {
						tooltipText += `${item.marker} ${item.seriesName} : $${formatNumberUnits(item.value)}<br/>`;
					});
					tooltipText = `<div style="text-align: left;">Year : ${year}<br/>${tooltipText}</div>`;
					return tooltipText;
				}
				const { name, value } = params[0];
				const dateLabel = moment(name).format('MMM-YYYY');
				return `${dateLabel}: $${formatNumberUnits(value)}`;
			},
			textStyle: {
				color: colors.widget.chart.tooltipText,
				textAlign: 'left'
			}
		},
		legend: {
			show: hasLegend,
			type: 'scroll',
			// Scroll arrow icons
			pageIconColor: colors.widget.chart.legendActiveUpDownIconColor,
			pageIconInactiveColor: colors.widget.chart.legendInactiveUpDownIconColor,
			pageTextStyle: {
				color: colors.widget.chart.legendTextColor,
				fontSize: 10
			},
			pageButtonItemGap: 5,
			pageButtonGap: 10,
			orient: 'horizontal',
			bottom: '4%',
			left: '0%',
			textStyle: {
				color: colors.widget.chart.legendTextColor
			},
			selectedMode: true,
			itemWidth: 10,
			itemHeight: 10,
			icon: 'roundRect'
		}
	};

	return { chartOption };
};


//3D barchart
interface BuildThreeDBarChartOptionsParams {
	data: {
		name: string;
		value: number;
	}[];
	barColors?: string[];
	colors: any;
	showGridLines?: boolean;
	showLegend?: boolean;
	showDataLabels?: boolean;
	tooltipFormatter?: (params: any) => string;
	valueType?: 'cost' | 'storage' | 'number';
	storageUnit?: 'KB' | 'MB' | 'GB' | 'TB' | 'PB';
	barWidth?: number;
	yAxisName?: string;
	yAxisMax?: number;
	yAxisInterval?: number;
	decimalPlaces?: number;
}

const STORAGE_UNIT_INDEX: Record<string, number> = {
	KB: 0,
	MB: 1,
	GB: 2,
	TB: 3,
	PB: 4,
};

const DEFAULT_BAR_COLORS = [
	'#8172F3',
	'#FF8D87',
	'#27B8D2',
	'#FFA63A',
	'#006C8C',
];

const normalizeHexColor = (color: string) => {
	let normalizedColor = color.replace('#', '');

	if (normalizedColor.length === 3) {
		normalizedColor = normalizedColor
			.split('')
			.map((character) => character + character)
			.join('');
	}

	return normalizedColor;
};

const hexToRgb = (color: string) => {
	const normalizedColor = normalizeHexColor(color);
	const colorValue = Number.parseInt(normalizedColor, 16);

	return {
		red: (colorValue >> 16) & 255,
		green: (colorValue >> 8) & 255,
		blue: colorValue & 255,
	};
};

const toRgba = (color: string, opacity: number) => {
	if (!color?.startsWith('#')) {
		return color;
	}

	const { red, green, blue } = hexToRgb(color);

	return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const shadeColor = (
	color: string,
	percentage: number,
) => {
	if (!color?.startsWith('#')) {
		return color;
	}

	const { red, green, blue } = hexToRgb(color);

	const adjustChannel = (channel: number) => {
		const target = percentage < 0 ? 0 : 255;
		const amount = Math.abs(percentage);

		return Math.round(
			channel + (target - channel) * amount,
		);
	};

	const adjustedRed = adjustChannel(red);
	const adjustedGreen = adjustChannel(green);
	const adjustedBlue = adjustChannel(blue);

	return `rgb(${adjustedRed}, ${adjustedGreen}, ${adjustedBlue})`;
};

export const buildThreeDBarChartOptions = ({
	data,
	barColors = [],
	colors,
	showGridLines = true,
	showLegend = true,
	showDataLabels = true,
	tooltipFormatter,
	valueType = 'number',
	storageUnit = 'GB',
	barWidth = 100,
	yAxisName,
	yAxisMax,
	yAxisInterval = 1,
	decimalPlaces = 2,
}: BuildThreeDBarChartOptionsParams) => {
	if (!data?.length) {
		return null;
	}

	const storageFactor = Math.pow(
		1024,
		STORAGE_UNIT_INDEX[storageUnit],
	);

	const formatValue = (rawValue: number) => {
		const value = Number(rawValue ?? 0);

		if (valueType === 'cost') {
			return `$${value.toLocaleString(undefined, {
				minimumFractionDigits: decimalPlaces,
				maximumFractionDigits: decimalPlaces,
			})}`;
		}

		if (valueType === 'storage') {
			return `${(value / storageFactor).toFixed(
				decimalPlaces,
			)} ${storageUnit}`;
		}

		if (value >= 1_000_000_000) {
			return `${(value / 1_000_000_000).toFixed(
				decimalPlaces,
			)}B`;
		}

		if (value >= 1_000_000) {
			return `${(value / 1_000_000).toFixed(
				decimalPlaces,
			)}M`;
		}

		if (value >= 1_000) {
			return `${(value / 1_000).toFixed(
				decimalPlaces,
			)}K`;
		}

		return value.toLocaleString(undefined, {
			minimumFractionDigits: 0,
			maximumFractionDigits: decimalPlaces,
		});
	};

	const labels = data.map((item) => item.name);

	const maximumDataValue = Math.max(
		...data.map((item) => Number(item.value ?? 0)),
	);

	const calculatedAxisMax =
		yAxisMax ??
		Math.ceil(maximumDataValue + maximumDataValue * 0.15);

	const chartSeries = data.map((item, index) => {
		const baseColor =
			barColors[index] ||
			DEFAULT_BAR_COLORS[
			index % DEFAULT_BAR_COLORS.length
			];

		const leftFaceColor = toRgba(baseColor, 0.58);
		const rightFaceColor = toRgba(baseColor, 0.82);
		const leftTopColor = shadeColor(baseColor, 0.12);
		const rightTopColor = shadeColor(baseColor, -0.1);

		return {
			name: item.name,
			type: 'custom',
			coordinateSystem: 'cartesian2d',
			data: [[index, Number(item.value ?? 0)]],
			encode: {
				x: 0,
				y: 1,
			},
			itemStyle: {
				color: baseColor,
			},

			renderItem: (_params: any, api: any) => {
				const categoryIndex = Number(api.value(0));
				const value = Number(api.value(1));

				const topPoint = api.coord([
					categoryIndex,
					value,
				]);

				const bottomPoint = api.coord([
					categoryIndex,
					0,
				]);

				const categoryWidth = Math.abs(
					api.size([1, 0])[0],
				);

				const actualBarWidth = Math.min(
					barWidth,
					categoryWidth * 0.9,
				);

				const halfWidth = actualBarWidth / 2;

				const centerX = topPoint[0];
				const topY = topPoint[1];
				const bottomY = bottomPoint[1];

				const leftX = centerX - halfWidth;
				const rightX = centerX + halfWidth;

				const barPixelHeight = Math.max(
					0,
					bottomY - topY,
				);

				const bevelHeight = Math.min(
					7,
					actualBarWidth * 0.07,
					barPixelHeight / 2,
				);

				const frontTopY = topY + bevelHeight;

				const children: any[] = [
					{
						type: 'polygon',
						shape: {
							points: [
								[leftX, frontTopY],
								[centerX, frontTopY],
								[centerX, bottomY],
								[leftX, bottomY],
							],
						},
						style: {
							fill: leftFaceColor,
						},
					},
					{
						type: 'polygon',
						shape: {
							points: [
								[centerX, frontTopY],
								[rightX, frontTopY],
								[rightX, bottomY],
								[centerX, bottomY],
							],
						},
						style: {
							fill: rightFaceColor,
						},
					},
					{
						type: 'polygon',
						shape: {
							points: [
								[leftX, frontTopY],
								[centerX, topY],
								[centerX, frontTopY],
							],
						},
						style: {
							fill: leftTopColor,
						},
					},
					{
						type: 'polygon',
						shape: {
							points: [
								[centerX, topY],
								[rightX, frontTopY],
								[centerX, frontTopY],
							],
						},
						style: {
							fill: rightTopColor,
						},
					},
				];

				if (showDataLabels) {
					children.push({
						type: 'text',
						style: {
							text: formatValue(value),
							x: centerX,
							y: topY - 7,
							textAlign: 'center',
							textVerticalAlign: 'bottom',
							fill: colors.widget.text.main,
							fontSize: 14,
							fontWeight: 500,
						},
					});
				}

				return {
					type: 'group',
					children,
				};
			},
		};
	});

	return {
		backgroundColor: 'transparent',

		animationDuration: 700,
		animationEasing: 'cubicOut',

		legend: {
			show: showLegend,
			bottom: 0,
			left: 'center',
			type: 'scroll',
			itemWidth: 10,
			itemHeight: 10,
			itemGap: 18,
			icon: 'rect',
			data: labels,
			textStyle: {
				color: colors.widget.text.main,
				fontSize: 11,
				fontWeight: 400,
			},
		},

		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'shadow',
				shadowStyle: {
					color: 'rgba(0, 0, 0, 0.03)',
				},
			},
			formatter:
				tooltipFormatter ||
				((params: any[]) => {
					if (!params?.length) {
						return '';
					}

					const selectedParam = params.find(
						(param: any) =>
							Array.isArray(param.value) &&
							param.value.length > 1,
					);

					if (!selectedParam) {
						return '';
					}

					const value = Number(
						selectedParam.value[1] ?? 0,
					);

					return `${selectedParam.marker} ${selectedParam.seriesName
						} : <b>${formatValue(value)}</b>`;
				}),
		},

		grid: {
			left: '4%',
			right: '4%',
			top: showDataLabels ? 30 : 15,
			bottom: showLegend ? 55 : 20,
			containLabel: true,
		},

		xAxis: {
			type: 'category',
			data: labels,
			boundaryGap: true,
			axisTick: {
				show: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: colors.widget.chart.labelColorSecondary,
					opacity: 0.4,
				},
			},
			axisLabel: {
				show: false,
			},
		},

		yAxis: {
			type: 'value',
			min: 0,
			max: calculatedAxisMax,
			interval: yAxisInterval,
			name: yAxisName,
			nameTextStyle: {
				color: colors.widget.chart.labelColorSecondary,
			},
			axisTick: {
				show: false,
			},
			axisLine: {
				show: false,
			},
			splitLine: {
				show: showGridLines,
				lineStyle: {
					type: 'dashed',
				},
			},
			axisLabel: {
				color: colors.widget.chart.labelColorSecondary,
				formatter: (value: number) => value,
			},
		},

		series: chartSeries,
	};
};

//3D piechart
interface ThreeDPieChartData {
	name: string;
	value: number;
	percentage?: number | string;
	color?: string;
}

interface BuildThreeDPieChartOptionsParams {
	theme: Theme;
	seriesData: ThreeDPieChartData[];
	pieColors?: string[];
	showDataLabels?: boolean;
	showLegend?: boolean;
	showTooltip?: boolean;
	pieRadius?: string | number;
	depth?: number;
	center?: [string, string];
	valueType?: 'cost' | 'number';
	decimalPlaces?: number;
	legendPosition?: 'left' | 'center' | 'right';
	labelLineLength?: number;
}

const DEFAULT_PIE_COLORS = [
	'#1239F4',
	'#FF8B84',
	'#16C4E6',
	'#3798FF',
	'#F4A23B',
	'#7559E8',
];

const normalizePieHexColor = (color: string) => {
	let normalizedColor = color.replace('#', '');

	if (normalizedColor.length === 3) {
		normalizedColor = normalizedColor
			.split('')
			.map((character) => character + character)
			.join('');
	}

	return normalizedColor;
};

const getPieRgb = (color: string) => {
	if (!color?.startsWith('#')) {
		return null;
	}

	const normalizedColor = normalizePieHexColor(color);

	if (normalizedColor.length !== 6) {
		return null;
	}

	const colorValue = Number.parseInt(
		normalizedColor,
		16,
	);

	return {
		red: (colorValue >> 16) & 255,
		green: (colorValue >> 8) & 255,
		blue: colorValue & 255,
	};
};

const darkenPieColor = (
	color: string,
	percentage = 0.28,
) => {
	const rgb = getPieRgb(color);

	if (!rgb) {
		return color;
	}

	const darkenChannel = (channel: number) =>
		Math.max(
			0,
			Math.round(
				channel * (1 - percentage),
			),
		);

	return `rgb(${darkenChannel(
		rgb.red,
	)}, ${darkenChannel(
		rgb.green,
	)}, ${darkenChannel(rgb.blue)})`;
};

const createPieDepthGradient = (
	color: string,
) => ({
	type: 'linear',
	x: 0,
	y: 0,
	x2: 1,
	y2: 0,
	colorStops: [
		{
			offset: 0,
			color: darkenPieColor(color, 0.65),
		},
		{
			offset: 0.45,
			color: darkenPieColor(color, 0.4),
		},
		{
			offset: 0.75,
			color: darkenPieColor(color, 0.22),
		},
		{
			offset: 1,
			color: darkenPieColor(color, 0.08),
		},
	],
	global: false,
});

const calculatePiePercentage = (
	value: number,
	total: number,
	decimalPlaces: number,
) => {
	if (!total) {
		return Number(0).toFixed(decimalPlaces);
	}

	return ((value / total) * 100).toFixed(
		decimalPlaces,
	);
};

const resolvePiePercentage = (
	item: ThreeDPieChartData,
	total: number,
	decimalPlaces: number,
) => {
	if (
		item.percentage !== undefined &&
		item.percentage !== null &&
		item.percentage !== ''
	) {
		const parsedPercentage = Number(
			String(item.percentage).replace(
				'%',
				'',
			),
		);

		if (!Number.isNaN(parsedPercentage)) {
			return parsedPercentage.toFixed(
				decimalPlaces,
			);
		}
	}

	return calculatePiePercentage(
		Number(item.value ?? 0),
		total,
		decimalPlaces,
	);
};

const getPieDepthCenter = (
	centerVertical: string,
	depthValue: number,
) => {
	const parsedCenter = Number(
		centerVertical.replace('%', ''),
	);

	if (Number.isNaN(parsedCenter)) {
		return centerVertical;
	}

	return `${parsedCenter + depthValue / 5}%`;
};

const hslToHex = (
	hue: number,
	saturation: number,
	lightness: number,
) => {
	const normalizedSaturation =
		saturation / 100;
	const normalizedLightness =
		lightness / 100;

	const chroma =
		(1 -
			Math.abs(
				2 * normalizedLightness - 1,
			)) *
		normalizedSaturation;

	const hueSection = hue / 60;

	const secondaryComponent =
		chroma *
		(1 - Math.abs((hueSection % 2) - 1));

	let red = 0;
	let green = 0;
	let blue = 0;

	if (hueSection < 1) {
		red = chroma;
		green = secondaryComponent;
	} else if (hueSection < 2) {
		red = secondaryComponent;
		green = chroma;
	} else if (hueSection < 3) {
		green = chroma;
		blue = secondaryComponent;
	} else if (hueSection < 4) {
		green = secondaryComponent;
		blue = chroma;
	} else if (hueSection < 5) {
		red = secondaryComponent;
		blue = chroma;
	} else {
		red = chroma;
		blue = secondaryComponent;
	}

	const matchValue =
		normalizedLightness - chroma / 2;

	const toHex = (value: number) =>
		Math.round(
			(value + matchValue) * 255,
		)
			.toString(16)
			.padStart(2, '0');

	return `#${toHex(red)}${toHex(
		green,
	)}${toHex(blue)}`;
};

const getGeneratedPieColor = (
	index: number,
) => {
	const hue = (index * 137.508) % 360;

	return hslToHex(hue, 68, 52);
};

const getPieColor = (
	index: number,
	itemColor: string | undefined,
	pieColors: string[],
) => {
	if (itemColor) {
		return itemColor;
	}

	if (pieColors[index]) {
		return pieColors[index];
	}

	if (DEFAULT_PIE_COLORS[index]) {
		return DEFAULT_PIE_COLORS[index];
	}

	return getGeneratedPieColor(index);
};

export const buildThreeDPieChartOptions = ({
	theme,
	seriesData,
	pieColors = [],
	showDataLabels = true,
	showLegend = true,
	showTooltip = true,
	pieRadius = '65%',
	depth = 18,
	center = ['50%', '43%'],
	valueType = 'number',
	decimalPlaces = 2,
	legendPosition = 'center',
	labelLineLength = 22,
}: BuildThreeDPieChartOptionsParams) => {
	if (!seriesData?.length) {
		return null;
	}

	const colors = theme.palette;

	const total = seriesData.reduce(
		(sum, item) =>
			sum + Number(item.value ?? 0),
		0,
	);

	const sortedSeriesData = [...seriesData].sort(
		(firstItem, secondItem) =>
			Number(secondItem.value ?? 0) -
			Number(firstItem.value ?? 0),
	);

	const resolvedData = sortedSeriesData.map(
		(item, index) => {
			const color = getPieColor(
				index,
				item.color,
				pieColors,
			);

			return {
				...item,
				value: Number(item.value ?? 0),
				percentage: resolvePiePercentage(
					item,
					total,
					decimalPlaces,
				),
				color,
				depthGradient:
					createPieDepthGradient(color),
			};
		},
	);

	const formatValue = (
		rawValue: number,
	) => {
		const value = Number(rawValue ?? 0);

		const formattedValue =
			value.toLocaleString(undefined, {
				minimumFractionDigits: 0,
				maximumFractionDigits:
					decimalPlaces,
			});

		return valueType === 'cost'
			? `$${formattedValue}`
			: formattedValue;
	};

	const depthCenter: [string, string] = [
		center[0],
		getPieDepthCenter(center[1], depth),
	];

	const depthSeriesData = resolvedData.map(
		(item) => ({
			name: item.name,
			value: item.value,
			itemStyle: {
				color: item.depthGradient,
			},
		}),
	);

	const mainSeriesData = resolvedData.map(
		(item) => ({
			name: item.name,
			value: item.value,
			percentage: item.percentage,
			itemStyle: {
				color: item.color,
			},
		}),
	);

	return {
		backgroundColor:
			colors.widget.background.chart,

		color: resolvedData.map(
			(item) => item.color,
		),

		tooltip: {
			show: showTooltip,
			trigger: 'item',
			backgroundColor:
				colors.widget.chart.tooltipBg,
			borderWidth: 0,
			textStyle: {
				color:
					colors.widget.chart
						.tooltipText,
			},
			formatter: (params: any) => {
				if (
					params.seriesName === 'Pie depth'
				) {
					return '';
				}

				const item = resolvedData.find(
					(currentItem) =>
						currentItem.name === params.name,
				);

				const percentage =
					item?.percentage ??
					Number(
						params.percent ?? 0,
					).toFixed(decimalPlaces);

				return `
					<div>
						${params.marker}
						${params.name} :
						${percentage}%
						(${formatValue(params.value)})
					</div>
				`;
			},
		},

		legend: {
			type: 'scroll',
			show: showLegend,
			orient: 'horizontal',
			bottom: -8,
			left: legendPosition,
			right: 20,
			itemWidth: 10,
			itemHeight: 10,
			itemGap: 14,
			selectedMode: false,
			data: resolvedData.map(
				(item) => item.name,
			),
			pageIconColor:
				colors.widget.chart
					.legendTextColor,
			pageIconInactiveColor:
				colors.widget.chart
					.legendTextColor,
			pageTextStyle: {
				color:
					colors.widget.chart
						.legendTextColor,
			},
			textStyle: {
				color:
					colors.widget.chart.legendTextColor,
				fontSize: 11,
			},
		},

		series: [
			{
				name: 'Pie depth',
				type: 'pie',
				radius: pieRadius,
				center: depthCenter,
				startAngle: 90,
				clockwise: true,
				silent: true,
				animation: false,
				z: 1,

				label: {
					show: false,
				},

				labelLine: {
					show: false,
				},

				tooltip: {
					show: false,
				},

				emphasis: {
					disabled: true,
				},

				itemStyle: {
					borderWidth: 0,
				},

				data: depthSeriesData,
			},
			{
				name: 'Pie data',
				type: 'pie',
				radius: pieRadius,
				center,
				z: 2,
				startAngle: 90,
				clockwise: true,
				avoidLabelOverlap: true,
				minAngle: 0,

				label: {
					show: showDataLabels,
					position: 'outside',
					alignTo: 'edge',
					edgeDistance: 12,
					bleedMargin: 5,
					color:
						colors.widget.chart
							.labelColorSecondary,
					fontSize: 12,
					lineHeight: 15,

					formatter: (params: any) => {
						const item =
							resolvedData.find(
								(currentItem) =>
									currentItem.name ===
									params.name,
							);

						const percentage =
							item?.percentage ??
							Number(
								params.percent ?? 0,
							).toFixed(decimalPlaces);

						return `{name|${params.name}} {percentage|${percentage}% (${formatValue(params.value)})}`;
					},

					rich: {
						name: {
							color:
								colors.widget.chart
									.labelColorSecondary,
							fontSize: 11,
							fontWeight: 400,
						},
						percentage: {
							color:
								colors.widget.text.value,
							fontSize: 11,
							fontWeight: 500,
						},
					},
				},

				labelLine: {
					show: showDataLabels,
					length: labelLineLength,
					smooth: false,
					minTurnAngle: 80,
					lineStyle: {
						width: 1,
					},
				},

				labelLayout: {
					hideOverlap: true,
					moveOverlap: 'shiftY',
					draggable: false,
				},

				itemStyle: {
					borderWidth: 0,
				},

				emphasis: {
					scale: true,
					scaleSize: 5,
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowOffsetY: 4,
						shadowColor:
							'rgba(0, 0, 0, 0.25)',
					},
				},

				data: mainSeriesData,
			},
		],

		textStyle: {
			fontFamily: 'Montserrat',
		},
	};
};